import React, { useState, useEffect, useCallback } from "react";
import GlobalStyles from "../GlobalStyles";

const PUBLIC_BASE_PATH = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
const API_URL = (
  process.env.REACT_APP_API_URL || `${PUBLIC_BASE_PATH}/backend`
).replace(/\/$/, "");

const Scenario3 = ({
  lph,
  maxMonthlyWaterLiters,
  pipeSizes,
  pipeClasses,
  selectedClass,
  classesByMaterial,
  allPipeSizes,
  onDataChange,
  onCalculationResults,
}) => {
  const [selectedOption, setSelectedOption] = useState("drip-lateral");
  const [subMains, setSubMains] = useState([1]);
  const [formData, setFormData] = useState({

    flowRate: lph || 0,

    lateralLength: "",
    lateralOuterDia: "",
    lateralThickness: "",
    lateralInnerDia: "",
    noOfPlantsPerLateral: "",
    drippersPerPlant: "",
    flowPerDripper: "",
    totalFlowPerLateral: "",

    mainLength: "",
    mainOuterDia: "",
    mainClass: "",
    mainSize: "",
    mainThickness: "",
    mainInnerDia: "",
    totalFlowMain: "",

    dripLineLength: "",
    dripLineOuterDia: "",
    dripLineThickness: "",
    dripLineInnerDia: "",
    dripperSpacing: "",
    drippersPerLateral: "",
    dripLineFlowPerDripper: "",
    dripLineTotalFlow: "",
  });

  const [subMainFilteredSizes, setSubMainFilteredSizes] = useState({});
  const [mainFilteredSizes, setMainFilteredSizes] = useState([]);

  const [subMainClassErrors, setSubMainClassErrors] = useState({});
  const [mainClassError, setMainClassError] = useState(false);
  const [subMainErrorMessages, setSubMainErrorMessages] = useState({});
  const [mainErrorMessage, setMainErrorMessage] = useState("");

  const [calculatedValues, setCalculatedValues] = useState({

    lateralHeadLoss: 0,
    lateralOutletFactor: 0,
    lateralNetHeadLoss: 0,

    dripLineHeadLoss: 0,
    dripLineOutletFactor: 0,
    dripLineNetHeadLoss: 0,

    subMainHeadLoss: {},
    subMainOutletFactor: {},
    subMainNetHeadLoss: {},
    subMainTotalFlow: {},
    subMainVelocity: {},

    mainHeadLoss: 0,
    mainNetHeadLoss: 0,

    majorFrictionLosses: 0,
    minorFrictionLosses: 0,
    totalFrictionLosses: 0,
    tdh: 0,
    systemLph: 0,
  });

  const [selectedPump, setSelectedPump] = useState("submersible");
  const [suctionPipeSize, setSuctionPipeSize] = useState("");
  const [suctionPipeClass, setSuctionPipeClass] = useState("");
  const [suctionPipeLength, setSuctionPipeLength] = useState("");
  const [deliveryPipeSize, setDeliveryPipeSize] = useState("");
  const [deliveryPipeClass, setDeliveryPipeClass] = useState("");
  const [deliveryPipeLength, setDeliveryPipeLength] = useState("");
  const [suctionHead, setSuctionHead] = useState("");
  const [deliveryHead, setDeliveryHead] = useState("");
  const [suctionClassError, setSuctionClassError] = useState(false);
  const [deliveryClassError, setDeliveryClassError] = useState(false);
  const [suctionErrorMessage, setSuctionErrorMessage] = useState("");
  const [deliveryErrorMessage, setDeliveryErrorMessage] = useState("");
  const [suctionFilteredSizes, setSuctionFilteredSizes] = useState([]);
  const [deliveryFilteredSizes, setDeliveryFilteredSizes] = useState([]);
  const [pumpCalculationResults, setPumpCalculationResults] = useState(null);
  const [pumpLoading, setPumpLoading] = useState(false);
  const [pumpError, setPumpError] = useState("");

  const [pumpEfficiency, setPumpEfficiency] = useState(70);
  const [motorEfficiency, setMotorEfficiency] = useState(85);
  const [solarEfficiency, setSolarEfficiency] = useState(70);

  const [numberOfDays, setNumberOfDays] = useState(30);
  const [numberOfHours, setNumberOfHours] = useState(6);
  const [daysInputValue, setDaysInputValue] = useState("30");
  const [hoursInputValue, setHoursInputValue] = useState("6");
  const [localLph, setLocalLph] = useState(lph);
  const [maxWaterInputValue, setMaxWaterInputValue] = useState(
    maxMonthlyWaterLiters.toString()
  );

  const getMaterialFromClass = (pipeClass) => {
    if (["CL6", "CL9", "CL12", "CL15"].includes(pipeClass)) return "uPVC";
    if (["EL", "L2", "M", "H"].includes(pipeClass)) return "GI";
    if (["PN-06", "PN-08", "PN-10", "PN-12.5", "PN-16"].includes(pipeClass))
      return "HDPE";
    return "Unknown";
  };

  const getClassDisplayName = (pipeClass) => {
    const displayNames = {
      CL6: "Class 6 (6 kg/cm²)",
      CL9: "Class 9 (9 kg/cm²)",
      CL12: "Class 12 (12 kg/cm²)",
      CL15: "Class 15 (15 kg/cm²)",
      EL: "Extra Light",
      L2: "Light",
      M: "Medium",
      H: "Heavy",
      "PN-06": "PN 6 (6 bar)",
      "PN-08": "PN 8 (8 bar)",
      "PN-10": "PN 10 (10 bar)",
      "PN-12.5": "PN 12.5 (12.5 bar)",
      "PN-16": "PN 16 (16 bar)",
    };
    return displayNames[pipeClass] || pipeClass;
  };

  const getMaterialIcon = (pipeClass) => {
    const material = getMaterialFromClass(pipeClass);
    const icons = {
      uPVC: "🔵",
      GI: "⚫",
      HDPE: "🟡",
    };
    return icons[material] || "⚪";
  };

  const calculateHeadLoss = (flow, length, innerDiameter) => {

    if (!flow || !length || !innerDiameter) return 0;

    const Q = parseFloat(flow);
    const L = parseFloat(length);
    const ID = parseFloat(innerDiameter);

    if (Q <= 0 || L <= 0 || ID <= 0) return 0;

    const headLoss =
      1.21e10 * Math.pow(Q / 3600 / 140, 1.85) * L * Math.pow(ID, -4.87);

    return headLoss;
  };

  const calculateOutletFactor = () => {
    if (selectedOption === "drip-lateral") {
      const n = parseFloat(formData.noOfPlantsPerLateral) || 1;

      if (n <= 0) return 1;
      return (1 / 2.85) + (1 / (2 * n)) + (Math.sqrt(0.85) / (6 * n * n));
    }
    if (selectedOption === "drip-line") {
      const s = parseFloat(formData.dripperSpacing) || 1;
      if (s <= 0) return 1;
      return (1 / 2.85) + (1 / (2 * s)) + (Math.sqrt(0.85) / (6 * s * s));
    }
    return 1;
  };
  
  const calculateNetHeadLoss = (headLoss, outletFactor) => {
    if (!headLoss || !outletFactor) return 0;
    return headLoss * outletFactor;
  };

  const calculatePowerRequirements = () => {

    const cropLph = lph || 0;

    let systemLph = 0;
    subMains.forEach((subMainNumber) => {
      const subMainTotalFlow =
        calculatedValues.subMainTotalFlow?.[subMainNumber] || 0;
      systemLph = Math.max(systemLph, subMainTotalFlow);
    });

    const tdh = calculatedValues.tdh || 0;

    if (cropLph <= 0 || tdh <= 0) return null;

    const hpw = (cropLph * tdh) / 270000;

    const hpp = hpw / (pumpEfficiency / 100);

    const hpm = hpp;

    const hps = hpm / (motorEfficiency / 100);

    const kwPvOutput = (hps * 746) / 1000;

    const kwPvInput = kwPvOutput / (solarEfficiency / 100);

    return {
      frictionLosses: calculatedValues.totalFrictionLosses,
      minorLosses: calculatedValues.minorFrictionLosses,
      tdh,
      cropLph,
      systemLph,
      hpw,
      hpp,
      hpm,
      hps,
      kwPvOutput,
      kwPvInput,
    };
  };

  const calculateTotalFlowPerLateral = (
    noOfPlants,
    drippersPerPlant,
    flowPerDripper
  ) => {
    const plants = parseFloat(noOfPlants) || 0;
    const drippers = parseFloat(drippersPerPlant) || 0;
    const flow = parseFloat(flowPerDripper) || 0;
    return plants * drippers * flow;
  };

  const calculateDrippersPerLateral = (length, spacing) => {
    const lateralLength = parseFloat(length) || 0;
    const dripperSpacing = parseFloat(spacing) || 0;
    if (dripperSpacing <= 0) return 0;
    return Math.floor((lateralLength * 100) / dripperSpacing);
  };

  const calculateDripLineTotalFlow = (drippersPerLateral, flowPerDripper) => {
    const drippers = parseFloat(drippersPerLateral) || 0;
    const flow = parseFloat(flowPerDripper) || 0;
    const result = drippers * flow;

    return Math.round(result * 100) / 100;
  };

  const getMaterialDisplayName = (pipeClass) => {
    return getMaterialFromClass(pipeClass);
  };

  const getDisplaySizeForHDPE = (size, material, sizeData = null) => {

    if (sizeData && typeof sizeData === 'object' && sizeData.display) {
      return sizeData.display;
    }

    if (typeof size === 'object' && size.display) {
      return size.display;
    }

    return `${size}"`;
  };

  const calculatePumpTDH = () => {
    if (!pumpCalculationResults) return 0;

    let suctionLoss = 0;
    if (selectedPump === "surface" && pumpCalculationResults.suction_pipe) {
      suctionLoss =
        parseFloat(pumpCalculationResults.suction_pipe.friction_losses_m) || 0;
    }

    const deliveryLoss =
      parseFloat(pumpCalculationResults.delivery_pipe.friction_losses_m) || 0;
    const frictionLosses = suctionLoss + deliveryLoss;
    const minorLosses = frictionLosses * 0.2;

    let tdh = frictionLosses + minorLosses;
    if (selectedPump === "surface") {
      tdh += parseFloat(suctionHead) + parseFloat(deliveryHead);
    } else {

      tdh += parseFloat(deliveryHead);
    }

    return tdh;
  };

  const getAvailableSizesForClass = useCallback(
    async (classType) => {
      if (!classType) return allPipeSizes;

      try {
        const response = await fetch(
          `${API_URL}/api/v1/pipe/available-sizes/${classType}`
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return data.available_sizes || [];
      } catch (error) {
        console.error("Error fetching available sizes:", error);
        return allPipeSizes;
      }
    },
    [allPipeSizes]
  );

  const handleOptionChange = (option) => {
    setSelectedOption(option);

    const updatedData = {
      ...formData,
      selectedOption: option,
      scenario: "scenario3",
      timestamp: new Date().toISOString(),
    };

    setFormData(updatedData);

    if (onDataChange) {
      onDataChange(updatedData);
    }
  };

  const handleInputChange = (field, value) => {

    setFormData(prevFormData => {
      const updatedData = {
        ...prevFormData,
        [field]: value,
        selectedOption,
        scenario: "scenario3",
        timestamp: new Date().toISOString(),
      };

      if (onDataChange) {
        onDataChange(updatedData);
      }

      return updatedData;
    });
  };

  const addSubMain = () => {
    const newSubMainNumber = subMains.length + 1;
    setSubMains([...subMains, newSubMainNumber]);

    const newFormData = { ...formData };
    const subMainFields = [
      "Length",
      "OuterDia",
      "Class",
      "Size",
      "Thickness",
      "InnerDia",
      "NoOfLateralOnly",
      "FlowPerLateral",
      "TotalFlow",
    ];

    subMainFields.forEach((field) => {
      newFormData[`subMain${newSubMainNumber}${field}`] = "";
    });

    setFormData(newFormData);
  };

  const removeSubMain = (subMainNumber) => {
    if (subMains.length > 1) {
      const newFormData = { ...formData };
      const subMainFields = [
        "Length",
        "OuterDia",
        "Class",
        "Size",
        "Thickness",
        "InnerDia",
        "NoOfLateralOnly",
        "FlowPerLateral",
        "TotalFlow",
      ];

      subMainFields.forEach((field) => {
        delete newFormData[`subMain${subMainNumber}${field}`];
      });

      const newSubMains = [];
      let newNumber = 1;

      subMains.forEach((oldNumber) => {
        if (oldNumber !== subMainNumber) {
          newSubMains.push(newNumber);

          if (oldNumber !== newNumber) {
            subMainFields.forEach((field) => {
              const oldKey = `subMain${oldNumber}${field}`;
              const newKey = `subMain${newNumber}${field}`;
              if (newFormData[oldKey] !== undefined) {
                newFormData[newKey] = newFormData[oldKey];
                delete newFormData[oldKey];
              }
            });
          }
          newNumber++;
        }
      });

      setSubMains(newSubMains);
      setFormData(newFormData);
    }
  };

  const handleDaysChange = (e) => {
    const rawInput = e.target.value;
    setDaysInputValue(rawInput);

    if (rawInput === "" || rawInput === ".") {
      setNumberOfDays(30);
    } else {
      const parsedValue = parseFloat(rawInput);
      if (!isNaN(parsedValue) && parsedValue > 0) {
        setNumberOfDays(parsedValue);
      }
    }
  };

  const handleDaysBlur = () => {
    if (daysInputValue === "" || daysInputValue === ".") {
      setDaysInputValue("30");
      setNumberOfDays(30);
    } else {
      const parsedValue = parseFloat(daysInputValue);
      if (!isNaN(parsedValue) && parsedValue > 0) {
        setDaysInputValue(parsedValue.toString());
        setNumberOfDays(parsedValue);
      } else {
        setDaysInputValue("30");
        setNumberOfDays(30);
      }
    }
  };

  const handleHoursChange = (e) => {
    const rawInput = e.target.value;
    setHoursInputValue(rawInput);

    if (rawInput === "" || rawInput === ".") {
      setNumberOfHours(6);
    } else {
      const parsedValue = parseFloat(rawInput);
      if (!isNaN(parsedValue) && parsedValue > 0) {
        setNumberOfHours(parsedValue);
      }
    }
  };

  const handleHoursBlur = () => {
    if (hoursInputValue === "" || hoursInputValue === ".") {
      setHoursInputValue("6");
      setNumberOfHours(6);
    } else {
      const parsedValue = parseFloat(hoursInputValue);
      if (!isNaN(parsedValue) && parsedValue > 0) {
        setHoursInputValue(parsedValue.toString());
        setNumberOfHours(parsedValue);
      } else {
        setHoursInputValue("6");
        setNumberOfHours(6);
      }
    }
  };

  useEffect(() => {
    if (lph !== formData.flowRate) {
      handleInputChange("flowRate", lph);
    }
  }, [lph, formData.flowRate, handleInputChange]);

  useEffect(() => {
    setLocalLph(lph);
  }, [lph]);

  useEffect(() => {
    setMaxWaterInputValue(maxMonthlyWaterLiters.toString());
  }, [maxMonthlyWaterLiters]);

  useEffect(() => {
    if (numberOfDays > 0 && numberOfHours > 0) {
      const maxWater = parseFloat(maxWaterInputValue) || maxMonthlyWaterLiters;
      const calculatedLph = maxWater / numberOfDays / numberOfHours;

      const formattedLph = parseFloat(calculatedLph.toFixed(2));

      setLocalLph(formattedLph);

      if (onDataChange) {
        onDataChange({ lph: formattedLph });
      }
    }
  }, [numberOfDays, numberOfHours, maxWaterInputValue, maxMonthlyWaterLiters, onDataChange]);

  useEffect(() => {
    const updateMainSizes = async () => {
      if (formData.mainClass) {
        const sizes = await getAvailableSizesForClass(formData.mainClass);
        setMainFilteredSizes(sizes);

        if (formData.mainSize) {
          const encodedMainSize = formData.mainSize.replace(/ /g, '_');
          const decodedSizes = sizes.map(size => typeof size === 'object' ? size.inch : size);
          const encodedSizes = sizes.map(size => typeof size === 'object' ? size.inch.replace(/ /g, '_') : size);

          const isAvailable = sizes.includes(formData.mainSize) ||
                             decodedSizes.includes(formData.mainSize) ||
                             encodedSizes.includes(encodedMainSize);

          if (!isAvailable) {
            handleInputChange("mainSize", "");
          }
        }
      } else {
        setMainFilteredSizes([]);
      }
    };

    updateMainSizes();
  }, [
    formData.mainClass,
    allPipeSizes,
    formData.mainSize,
    getAvailableSizesForClass,
  ]);

  useEffect(() => {
    const updateSubMainSizes = async () => {
      const newFilteredSizes = {};

      for (const subMainNumber of subMains) {
        const subMainClass = formData[`subMain${subMainNumber}Class`];
        if (subMainClass) {
          const sizes = await getAvailableSizesForClass(subMainClass);
          newFilteredSizes[subMainNumber] = sizes;

          const currentSize = formData[`subMain${subMainNumber}Size`];
          if (currentSize && !sizes.includes(currentSize)) {
            handleInputChange(`subMain${subMainNumber}Size`, "");
          }
        } else {
          newFilteredSizes[subMainNumber] = [];
        }
      }

      setSubMainFilteredSizes(newFilteredSizes);
    };

    updateSubMainSizes();
  }, [
    subMains,

    ...subMains.map((num) => formData[`subMain${num}Class`]),
    allPipeSizes,
    getAvailableSizesForClass,
  ]);

  useEffect(() => {
    if (selectedOption === "drip-lateral") {
      const outerDia = parseFloat(formData.lateralOuterDia) || 0;
      const thickness = parseFloat(formData.lateralThickness) || 0;
      const innerDia = outerDia - 2 * thickness;

      if (innerDia !== parseFloat(formData.lateralInnerDia)) {
        handleInputChange("lateralInnerDia", innerDia.toString());
      }
    }
  }, [
    selectedOption,
    formData.lateralOuterDia,
    formData.lateralThickness,
    formData.lateralInnerDia,
  ]);

  useEffect(() => {
    if (selectedOption === "drip-line") {
      const outerDia = parseFloat(formData.dripLineOuterDia) || 0;
      const thickness = parseFloat(formData.dripLineThickness) || 0;
      const innerDia = outerDia - 2 * thickness;

      if (innerDia !== parseFloat(formData.dripLineInnerDia)) {
        handleInputChange("dripLineInnerDia", innerDia.toString());
      }
    }
  }, [
    selectedOption,
    formData.dripLineOuterDia,
    formData.dripLineThickness,
    formData.dripLineInnerDia,
  ]);

  useEffect(() => {
    const fetchSubMainSpecs = async () => {
      for (const subMainNumber of subMains) {

        let outerDiaValue = formData[`subMain${subMainNumber}OuterDia`];

        if (typeof outerDiaValue === 'string' && outerDiaValue.includes('/')) {

          if (outerDiaValue === "1/2") outerDiaValue = 0.5;
          else if (outerDiaValue === "3/4") outerDiaValue = 0.75;
          else if (outerDiaValue === "1 1/4") outerDiaValue = 1.25;
          else if (outerDiaValue === "1 1/2") outerDiaValue = 1.5;
          else if (outerDiaValue === "2 1/2") outerDiaValue = 2.5;
          else if (outerDiaValue === "3 1/2") outerDiaValue = 3.5;
          else if (outerDiaValue === "4/5") outerDiaValue = 0.8;
          else if (outerDiaValue === "1 4/7") outerDiaValue = 1.5;
          else if (outerDiaValue === "4 1/3") outerDiaValue = 4.333;
          else if (outerDiaValue === "5 1/2") outerDiaValue = 5.5;
          else if (outerDiaValue === "6 2/7") outerDiaValue = 6.2;
          else {

            try {
              const parts = outerDiaValue.split(' ');
              if (parts.length === 2) {
                const wholePart = parseInt(parts[0]);
                const fractionParts = parts[1].split('/');
                if (fractionParts.length === 2) {
                  const numerator = parseInt(fractionParts[0]);
                  const denominator = parseInt(fractionParts[1]);
                  outerDiaValue = wholePart + numerator / denominator;
                }
              } else if (parts.length === 1 && outerDiaValue.includes('/')) {
                const fractionParts = outerDiaValue.split('/');
                if (fractionParts.length === 2) {
                  const numerator = parseInt(fractionParts[0]);
                  const denominator = parseInt(fractionParts[1]);
                  outerDiaValue = numerator / denominator;
                }
              }
            } catch (e) {
              outerDiaValue = parseFloat(outerDiaValue) || 0;
            }
          }
        }

        const outerDiaInch = parseFloat(outerDiaValue) || 0;
        const pipeClass = formData[`subMain${subMainNumber}Class`];

        if (!outerDiaInch || !pipeClass) {
          continue;
        }

        const baseClass = pipeClass.startsWith("PN-")
          ? pipeClass
          : pipeClass.split("-")[0];

        const convertToFraction = (input) => {

          if (typeof input === 'string' && (input.includes('/') || input.includes(' '))) {
            return input;
          }

          const numDecimal = parseFloat(input);

          if (numDecimal === 0.5) return "1/2";
          if (numDecimal === 0.75) return "3/4";
          if (numDecimal === 1) return "1";
          if (numDecimal === 1.25) return "1 1/4";
          if (numDecimal === 1.5) return "1 1/2";
          if (numDecimal === 2) return "2";
          if (numDecimal === 2.5) return "2 1/2";
          if (numDecimal === 3) return "3";
          if (numDecimal === 3.5) return "3 1/2";
          if (numDecimal === 4) return "4";
          if (numDecimal === 5) return "5";
          if (numDecimal === 5.5) return "5 1/2";
          if (numDecimal === 6) return "6";
          if (numDecimal === 8) return "8";
          if (numDecimal === 10) return "10";
          if (numDecimal === 12) return "12";

          if (Math.abs(numDecimal - 0.8) < 0.001) return "4/5";
          if (Math.abs(numDecimal - 1.571) < 0.001) return "1 4/7";
          if (Math.abs(numDecimal - 4.333) < 0.001) return "4 1/3";
          if (Math.abs(numDecimal - 6.28) < 0.001) return "6 2/7";

          return input.toString();
        };

        const fractionalSize = convertToFraction(outerDiaInch);

        try {
          const response = await fetch(
            `${API_URL}/api/v1/pipe-utils/thickness?inch=${encodeURIComponent(
              fractionalSize
            )}&class_type=${baseClass}`
          );

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(
              `HTTP error! status: ${response.status}, body: ${errorText}`
            );
          }

          const data = await response.json();

          if (data.error) {
            handleInputChange(
              `subMain${subMainNumber}InnerDia`,
              `Error: ${data.error}`
            );
            continue;
          }

          const innerDiaMm = data.inner_diameter_mm;

          if (innerDiaMm && innerDiaMm > 0) {
            const currentInnerDia =
              parseFloat(formData[`subMain${subMainNumber}InnerDia`]) || 0;
            const shouldUpdate = Math.abs(innerDiaMm - currentInnerDia) > 0.05;

            if (shouldUpdate || currentInnerDia > 200) {
              handleInputChange(
                `subMain${subMainNumber}InnerDia`,
                innerDiaMm.toFixed(1)
              );
            } else {
            }
          }
        } catch (error) {
          console.error(
            `Sub Main ${subMainNumber} Pipe Specs - Error fetching pipe specifications:`,
            error
          );

          handleInputChange(
            `subMain${subMainNumber}InnerDia`,
            "Error: Network error or invalid size"
          );
        }
      }
    };

    if (subMains.length > 0) {
      fetchSubMainSpecs();
    }
  }, [
    JSON.stringify(subMains),
    JSON.stringify(
      subMains.map((num) => ({
        outerDia: formData[`subMain${num}OuterDia`],
        class: formData[`subMain${num}Class`],
      }))
    ),
  ]);

  useEffect(() => {
    const outerDiaInch = parseFloat(formData.mainOuterDia) || 0;
    const pipeClass = formData.mainClass;

    if (!outerDiaInch || !pipeClass) {
      return;
    }

    const baseClass = pipeClass.startsWith("PN-")
      ? pipeClass
      : pipeClass.split("-")[0];

    const convertToFraction = (input) => {
      if (typeof input === 'string' && (input.includes('/') || input.includes(' '))) {
        return input;
      }

      const numDecimal = parseFloat(input);
      if (numDecimal === 0.5) return "1/2";
      if (numDecimal === 0.75) return "3/4";
      if (numDecimal === 1) return "1";
      if (numDecimal === 1.25) return "1 1/4";
      if (numDecimal === 1.5) return "1 1/2";
      if (numDecimal === 2) return "2";
      if (numDecimal === 2.5) return "2 1/2";
      if (numDecimal === 3) return "3";
      if (numDecimal === 3.5) return "3 1/2";
      if (numDecimal === 4) return "4";
      if (numDecimal === 5) return "5";
      if (numDecimal === 5.5) return "5 1/2";
      if (numDecimal === 6) return "6";
      if (numDecimal === 8) return "8";
      if (numDecimal === 10) return "10";
      if (numDecimal === 12) return "12";

      if (Math.abs(numDecimal - 0.8) < 0.001) return "4/5";
      if (Math.abs(numDecimal - 1.571) < 0.001) return "1 4/7";
      if (Math.abs(numDecimal - 4.333) < 0.001) return "4 1/3";
      if (Math.abs(numDecimal - 6.28) < 0.001) return "6 2/7";

      return input.toString();
    };

    const fractionalSize = convertToFraction(outerDiaInch);

    const fetchPipeSpecs = async () => {
      try {
        const response = await fetch(
          `${API_URL}/api/v1/pipe-utils/thickness?inch=${encodeURIComponent(fractionalSize)}&class_type=${baseClass}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.error) {
          handleInputChange("mainInnerDia", `Error: ${data.error}`);
          return;
        }

        const innerDiaMm = data.inner_diameter_mm;
        const thickness = data.thickness_mm;
        const outerDiaMm = data.outer_diameter_mm;

        if (innerDiaMm && innerDiaMm > 0) {
          const currentInnerDia = parseFloat(formData.mainInnerDia) || 0;
          const shouldUpdate = Math.abs(innerDiaMm - currentInnerDia) > 0.05;

          if (shouldUpdate) {
            handleInputChange("mainInnerDia", innerDiaMm.toFixed(1));
          }
        }
      } catch (error) {
        console.error(
          "💥 Main Pipe Specs - Error fetching pipe specifications:",
          error
        );
      }
    };

    fetchPipeSpecs();
  }, [formData.mainOuterDia, formData.mainClass]);

  useEffect(() => {
    if (selectedOption === "drip-lateral") {

      const totalFlow = calculateTotalFlowPerLateral(
        formData.noOfPlantsPerLateral,
        formData.drippersPerPlant,
        formData.flowPerDripper
      );

      if (totalFlow !== parseFloat(formData.totalFlowPerLateral)) {
        handleInputChange("totalFlowPerLateral", totalFlow.toString());
      }

      const headLoss = calculateHeadLoss(
        totalFlow,
        formData.lateralLength,
        formData.lateralInnerDia
      );

      const outletFactor = calculateOutletFactor();

      const netHeadLoss = calculateNetHeadLoss(headLoss, outletFactor);

      setCalculatedValues((prev) => ({
        ...prev,
        lateralHeadLoss: headLoss,
        lateralOutletFactor: outletFactor,
        lateralNetHeadLoss: netHeadLoss,
      }));
    }
  }, [
    selectedOption,
    formData.noOfPlantsPerLateral,
    formData.drippersPerPlant,
    formData.flowPerDripper,
    formData.lateralLength,
    formData.lateralInnerDia,
    formData.totalFlowPerLateral,
  ]);

  useEffect(() => {
    if (selectedOption === "drip-line") {

      const drippersPerLateral = calculateDrippersPerLateral(
        formData.dripLineLength,
        formData.dripperSpacing
      );

      if (drippersPerLateral !== parseFloat(formData.drippersPerLateral)) {
        handleInputChange("drippersPerLateral", drippersPerLateral.toString());
      }

      const totalFlow = calculateDripLineTotalFlow(
        drippersPerLateral,
        formData.dripLineFlowPerDripper
      );

      if (totalFlow !== parseFloat(formData.dripLineTotalFlow)) {
        handleInputChange("dripLineTotalFlow", totalFlow.toString());
      }

      const headLoss = calculateHeadLoss(
        totalFlow,
        formData.dripLineLength,
        formData.dripLineInnerDia
      );

      const outletFactor = calculateOutletFactor();

      const netHeadLoss = calculateNetHeadLoss(headLoss, outletFactor);

      setCalculatedValues((prev) => ({
        ...prev,
        dripLineHeadLoss: headLoss,
        dripLineOutletFactor: outletFactor,
        dripLineNetHeadLoss: netHeadLoss,
      }));
    }
  }, [
    selectedOption,
    formData.dripLineLength,
    formData.dripperSpacing,
    formData.drippersPerLateral,
    formData.dripLineFlowPerDripper,
    formData.dripLineTotalFlow,
    formData.dripLineInnerDia,
  ]);

  useEffect(() => {
    const newSubMainHeadLoss = {};
    const newSubMainOutletFactor = {};
    const newSubMainNetHeadLoss = {};
    const newSubMainTotalFlow = {};
    const newSubMainVelocity = {};

    let flowPerLateral = 0;
    if (selectedOption === "drip-lateral") {
      flowPerLateral = parseFloat(formData.totalFlowPerLateral) || 0;
    } else if (selectedOption === "drip-line") {
      flowPerLateral = parseFloat(formData.dripLineTotalFlow) || 0;
    }

    let cumulativeFlow = 0;

    subMains.forEach((subMainNumber) => {
      const length = formData[`subMain${subMainNumber}Length`];
      const innerDia = formData[`subMain${subMainNumber}InnerDia`];
      const noOfLaterals = formData[`subMain${subMainNumber}NoOfLateralOnly`];

      const thisSubMainLaterals = parseFloat(noOfLaterals) || 0;
      const thisSubMainFlow = thisSubMainLaterals * flowPerLateral;

      cumulativeFlow += thisSubMainFlow;

      newSubMainTotalFlow[subMainNumber] = cumulativeFlow;

      const currentTotalFlow = formData[`subMain${subMainNumber}TotalFlow`];
      if (cumulativeFlow !== parseFloat(currentTotalFlow)) {
        handleInputChange(
          `subMain${subMainNumber}TotalFlow`,
          cumulativeFlow.toString()
        );
      }

      const headLoss = calculateHeadLoss(thisSubMainFlow, length, innerDia);
      newSubMainHeadLoss[subMainNumber] = headLoss;

      const calculateOutletFactorForSubMain = (noOfLaterals) => {
        const n = parseFloat(noOfLaterals) || 1;
        return (1 / 2.85) + (1 / (2 * n)) + (Math.sqrt(0.85) / (6 * n * n));
      };

      const outletFactor = calculateOutletFactorForSubMain(noOfLaterals); 
      newSubMainOutletFactor[subMainNumber] = outletFactor;

      const netHeadLoss = calculateNetHeadLoss(headLoss, outletFactor);
      newSubMainNetHeadLoss[subMainNumber] = netHeadLoss;

      const innerDiaMm = parseFloat(innerDia) || 0;
      if (innerDiaMm > 0 && cumulativeFlow > 0) {
        const velocity = (0.277777 * cumulativeFlow) / (3.14 * innerDiaMm * innerDiaMm / 4);
        newSubMainVelocity[subMainNumber] = velocity;
      } else {
        newSubMainVelocity[subMainNumber] = 0;
      }
    });

    setCalculatedValues((prev) => ({
      ...prev,
      subMainHeadLoss: newSubMainHeadLoss,
      subMainOutletFactor: newSubMainOutletFactor,
      subMainNetHeadLoss: newSubMainNetHeadLoss,
      subMainTotalFlow: newSubMainTotalFlow,
      subMainVelocity: newSubMainVelocity,
    }));
  }, [
    subMains,
    selectedOption,
    formData.totalFlowPerLateral,
    formData.dripLineTotalFlow,
    ...subMains.flatMap((num) => [
      formData[`subMain${num}Length`],
      formData[`subMain${num}InnerDia`],
      formData[`subMain${num}NoOfLateralOnly`],
      formData[`subMain${num}TotalFlow`],
    ]),
  ]);

  useEffect(() => {

    let totalMainFlow = 0;
    subMains.forEach((subMainNumber) => {
      const subMainFlow = calculatedValues.subMainTotalFlow[subMainNumber] || 0;
      totalMainFlow = Math.max(totalMainFlow, subMainFlow);
    });

    if (totalMainFlow !== parseFloat(formData.totalFlowMain)) {
      handleInputChange("totalFlowMain", totalMainFlow.toString());
    }

    const mainHeadLoss = calculateHeadLoss(
      totalMainFlow,
      formData.mainLength,
      formData.mainInnerDia
    );

    const mainNetHeadLoss = mainHeadLoss;

    setCalculatedValues((prev) => ({
      ...prev,
      mainHeadLoss: mainHeadLoss,
      mainNetHeadLoss: mainNetHeadLoss,
    }));
  }, [
    subMains,
    calculatedValues.subMainTotalFlow,
    formData.totalFlowMain,
    formData.mainLength,
    formData.mainInnerDia,
  ]);

  useEffect(() => {

    let majorFrictionLosses = 0;

    if (selectedOption === "drip-lateral") {
      majorFrictionLosses += calculatedValues.lateralNetHeadLoss || 0;
    } else if (selectedOption === "drip-line") {
      majorFrictionLosses += calculatedValues.dripLineNetHeadLoss || 0;
    }

    subMains.forEach((subMainNumber) => {
      majorFrictionLosses +=
        calculatedValues.subMainNetHeadLoss[subMainNumber] || 0;
    });

    majorFrictionLosses += calculatedValues.mainNetHeadLoss || 0;

    const minorFrictionLosses = majorFrictionLosses * 0.2;

    const totalFrictionLosses = majorFrictionLosses + minorFrictionLosses;

    const nozzlePressure = parseFloat(formData.nozzlePressure) || 0;
    const elevationDifference = parseFloat(formData.elevationDifference) || 0;
    const filtrationLosses = parseFloat(formData.filtrationLosses) || 0;
    const fertigationLosses = parseFloat(formData.fertigationLosses) || 0;

    const pumpTdh = calculatePumpTDH();

    const tdh =
      nozzlePressure +
      elevationDifference +
      filtrationLosses +
      fertigationLosses +
      totalFrictionLosses +
      pumpTdh;

    let systemLph = 0;
    subMains.forEach((subMainNumber) => {
      const subMainTotalFlow =
        calculatedValues.subMainTotalFlow?.[subMainNumber] || 0;
      systemLph = Math.max(systemLph, subMainTotalFlow);
    });

    setCalculatedValues((prev) => ({
      ...prev,
      majorFrictionLosses: majorFrictionLosses,
      minorFrictionLosses: minorFrictionLosses,
      totalFrictionLosses: totalFrictionLosses,
      tdh: tdh,
      systemLph: systemLph,
    }));
  }, [
    selectedOption,
    calculatedValues.lateralNetHeadLoss,
    calculatedValues.dripLineNetHeadLoss,
    calculatedValues.subMainNetHeadLoss,
    calculatedValues.mainNetHeadLoss,
    calculatedValues.subMainTotalFlow,
    subMains,
    formData.nozzlePressure,
    formData.elevationDifference,
    formData.filtrationLosses,
    formData.fertigationLosses,
    lph,
    pumpCalculationResults,
    selectedPump,
    suctionHead,
    deliveryHead,
  ]);

  useEffect(() => {
    const updateSuctionSizes = async () => {
      if (suctionPipeClass) {
        const sizes = await getAvailableSizesForClass(suctionPipeClass);
        setSuctionFilteredSizes(sizes || []);
      } else {
        setSuctionFilteredSizes([]);
      }
    };

    updateSuctionSizes();
  }, [suctionPipeClass, getAvailableSizesForClass]);

  useEffect(() => {
    const updateDeliverySizes = async () => {
      if (deliveryPipeClass) {
        const sizes = await getAvailableSizesForClass(deliveryPipeClass);
        setDeliveryFilteredSizes(sizes || []);
      } else {
        setDeliveryFilteredSizes([]);
      }
    };

    updateDeliverySizes();
  }, [deliveryPipeClass, getAvailableSizesForClass]);

  const calculatePumpSpecs = async () => {

    if (selectedPump === "surface") {
      if (!suctionPipeSize || !suctionPipeClass || !deliveryPipeSize || !deliveryPipeClass) {
        setPumpError("Please select all pipe sizes and classes for surface pump");
        return;
      }
    } else {
      if (!deliveryPipeSize || !deliveryPipeClass) {
        setPumpError("Please select delivery pipe size and class");
        return;
      }
    }

    if (!deliveryPipeLength || !deliveryHead) {
      setPumpError("Please enter delivery pipe length and delivery head");
      return;
    }

    setPumpLoading(true);
    setPumpError("");

    try {
      const systemLph = calculatedValues.systemLph || lph || 0;

      if (systemLph === 0) {
        setPumpError("System LPH is 0. Please complete the irrigation system design first.");
        setPumpLoading(false);
        return;
      }

      const convertToFraction = (input) => {

        if (typeof input === 'string' && (input.includes('/') || input.includes(' '))) {
          return input;
        }

        const numDecimal = parseFloat(input);

        if (numDecimal === 0.5) return "1/2";
        if (numDecimal === 0.75) return "3/4";
        if (numDecimal === 1) return "1";
        if (numDecimal === 1.25) return "1 1/4";
        if (numDecimal === 1.5) return "1 1/2";
        if (numDecimal === 2) return "2";
        if (numDecimal === 2.5) return "2 1/2";
        if (numDecimal === 3) return "3";
        if (numDecimal === 3.5) return "3 1/2";
        if (numDecimal === 4) return "4";
        if (numDecimal === 5) return "5";
        if (numDecimal === 5.5) return "5 1/2";
        if (numDecimal === 6) return "6";
        if (numDecimal === 8) return "8";
        if (numDecimal === 10) return "10";
        if (numDecimal === 12) return "12";

        if (Math.abs(numDecimal - 0.8) < 0.001) return "4/5";
        if (Math.abs(numDecimal - 1.571) < 0.001) return "1 4/7";
        if (Math.abs(numDecimal - 4.333) < 0.001) return "4 1/3";
        if (Math.abs(numDecimal - 6.28) < 0.001) return "6 2/7";

        return input.toString();
      };

      const deliveryFractionalSize = convertToFraction(deliveryPipeSize);
      const deliveryResponse = await fetch(
        `${API_URL}/api/v1/pipe-utils/thickness?inch=${encodeURIComponent(deliveryFractionalSize)}&class_type=${deliveryPipeClass}`
      );

      if (!deliveryResponse.ok) {
        throw new Error(`Delivery pipe API error: ${deliveryResponse.status}`);
      }

      const deliveryData = await deliveryResponse.json();

      let suctionData = null;
      if (selectedPump === "surface") {
        const suctionFractionalSize = convertToFraction(suctionPipeSize);
        const suctionResponse = await fetch(
          `${API_URL}/api/v1/pipe-utils/thickness?inch=${encodeURIComponent(suctionFractionalSize)}&class_type=${suctionPipeClass}`
        );

        if (!suctionResponse.ok) {
          throw new Error(`Suction pipe API error: ${suctionResponse.status}`);
        }

        suctionData = await suctionResponse.json();
      }

      const deliveryLength = parseFloat(deliveryPipeLength) || 0;
      const deliveryInnerDia = deliveryData.inner_diameter_mm;
      const deliveryFrictionLoss = calculateHeadLoss(systemLph, deliveryLength, deliveryInnerDia);

      let suctionFrictionLoss = 0;
      if (selectedPump === "surface" && suctionData) {
        const suctionLength = parseFloat(suctionPipeLength) || 0;
        const suctionInnerDia = suctionData.inner_diameter_mm;
        suctionFrictionLoss = calculateHeadLoss(systemLph, suctionLength, suctionInnerDia);
      }

      const results = {
        delivery_pipe: {
          ...deliveryData,
          friction_losses_m: deliveryFrictionLoss,
          length_m: deliveryLength,
        },
        system_lph: systemLph,
      };

      if (selectedPump === "surface" && suctionData) {
        results.suction_pipe = {
          ...suctionData,
          friction_losses_m: suctionFrictionLoss,
          length_m: parseFloat(suctionPipeLength) || 0,
        };
      }

      setPumpCalculationResults(results);
      setPumpError("");
    } catch (error) {
      console.error("❌ Pump API Error:", error);
      setPumpError(`Error calculating pump specifications: ${error.message}`);
      setPumpCalculationResults(null);
    } finally {
      setPumpLoading(false);
    }
  };

  const containerStyle = {
    backgroundColor: "#f9f9f9",
    borderRadius: "8px",
    border: "1px solid #ddd",
  };

  const sectionStyle = {
    marginBottom: "20px",
    padding: "5px",
    backgroundColor: "white",
    borderRadius: "5px",
    border: "1px solid #e0e0e0",
  };

  const radioGroupStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
    marginTop: "15px",
  };

  const radioOptionStyle = {
    display: "flex",
    alignItems: "center",
    padding: "12px",
    border: "2px solid #e0e0e0",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.3s ease",
    backgroundColor: "white",
  };

  const radioOptionActiveStyle = {
    ...radioOptionStyle,
    borderColor: "#2196F3",
    backgroundColor: "#f0f8ff",
  };

  const radioInputStyle = {
    marginRight: "12px",
    transform: "scale(1.2)",
  };

  const labelStyle = {
    fontWeight: "500",
    fontSize: "16px",
    cursor: "pointer",
  };

  const descriptionStyle = {
    fontSize: "14px",
    color: "#666",
    marginTop: "5px",
  };

  const cardStyle = {
    flex: 1,
    padding: "20px",
    backgroundColor: "#fff",
    borderRadius: "8px",
    border: "1px solid #ddd",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  };

  const cardHeaderStyle = {
    fontSize: "16px",
    fontWeight: "600",
    marginBottom: "15px",
    color: "#333",
    borderBottom: "2px solid #4CAF50",
    paddingBottom: "8px",
  };

  const selectStyle = {
    width: "100%",
    padding: "8px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontSize: "14px",
    backgroundColor: "#fff",
  };

  const inputStyle = {
    width: "100%",
    padding: "8px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontSize: "14px",
  };

  return (
    <div style={containerStyle}>
      {}
      <div
        style={{
          padding: "15px",
          borderRadius: "6px",
          border: "1px solid #e8e8e8",
          marginBottom: "20px",
        }}
      >
        <h4 style={{ marginTop: 0, marginBottom: "15px", color: "#333" }}>
          Irrigation Scheduling
        </h4>

        <div
          style={{
            display: "flex",
            gap: "15px",
            marginBottom: "15px",
            alignItems: "flex-end",
          }}
        >
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>
              Irrigation days (per month):
              <input
                style={GlobalStyles.input}
                value={daysInputValue}
                onChange={handleDaysChange}
                onBlur={handleDaysBlur}
                type="text"
                inputMode="decimal"
                min="1"
              />
            </label>
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>
              Irrigation Hours (per day):
              <input
                style={GlobalStyles.input}
                value={hoursInputValue}
                onChange={handleHoursChange}
                onBlur={handleHoursBlur}
                type="text"
                inputMode="decimal"
                min="1"
                max="24"
              />
            </label>
          </div>
        </div>
      </div>

      {}
      <div style={sectionStyle}>
        <h4 style={{ marginBottom: "15px", color: "#555" }}>
          Surface & Delivery Pump Configuration
        </h4>

        {}
        <div style={radioGroupStyle}>
          {}
          <div
            style={
              selectedPump === "submersible"
                ? radioOptionActiveStyle
                : radioOptionStyle
            }
            onClick={() => setSelectedPump("submersible")}
          >
            <input
              type="radio"
              id="submersible"
              name="pumpType"
              value="submersible"
              checked={selectedPump === "submersible"}
              onChange={(e) => setSelectedPump(e.target.value)}
              style={radioInputStyle}
            />
            <div>
              <label htmlFor="submersible" style={labelStyle}>
                Submersible Pump
              </label>
              <div style={descriptionStyle}>
                Pump installed below water level, typically in a borewell or deep water source
              </div>
            </div>
          </div>

          {}
          <div
            style={
              selectedPump === "surface"
                ? radioOptionActiveStyle
                : radioOptionStyle
            }
            onClick={() => setSelectedPump("surface")}
          >
            <input
              type="radio"
              id="surface"
              name="pumpType"
              value="surface"
              checked={selectedPump === "surface"}
              onChange={(e) => setSelectedPump(e.target.value)}
              style={radioInputStyle}
            />
            <div>
              <label htmlFor="surface" style={labelStyle}>
                Surface Pump
              </label>
              <div style={descriptionStyle}>
                Pump installed above ground level, draws water through suction pipe from water source
              </div>
            </div>
          </div>
        </div>

        {}
        <p style={{ marginBottom: "20px", marginTop: "20px" }}>
          Crop LPH Requirement:{" "}
          <label style={labelStyle}>
            <input
              type="number"
              name="systemLph"
              value={localLph.toFixed(2) || 0}
              onChange={(e) => {
                const newLph = parseFloat(e.target.value) || 0;
                setLocalLph(newLph);

                if (onDataChange) {
                  onDataChange({ lph: newLph });
                }
              }}
              style={{
                width: "120px",
                display: "inline-block",
                marginLeft: "8px",
                marginRight: "8px",
                backgroundColor: "#ffffff",
                padding: "8px",
                border: "1px solid #ddd",
                borderRadius: "4px",
              }}
              inputMode="decimal"
            />
            LPH
          </label>
          <br />
          <span style={{ color: "black", fontSize: "10px", marginLeft: "8px" }}>
            Note: If hydrozones are more than 1, use crop LPH requirement accordingly.
          </span>
        </p>

        {}
        <div style={{ display: "flex", gap: "20px", marginBottom: "20px" }}>
          {}
          {selectedPump === "surface" && (
            <div style={cardStyle}>
              <div style={cardHeaderStyle}>Suction Pipe</div>
              <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
                <div>
                  <label style={labelStyle}>
                    Pressure Class:
                    <select
                      style={{
                        ...selectStyle,
                        borderColor: suctionClassError ? "#ff4d4f" : undefined,
                      }}
                      value={suctionPipeClass}
                      onChange={(e) => {
                        setSuctionPipeClass(e.target.value);
                        setSuctionClassError(false);
                        setSuctionErrorMessage("");
                      }}
                    >
                      <option value="">Select pipe class</option>
                      <optgroup label="uPVC Pipe">
                        {["CL6", "CL9", "CL12", "CL15"].map((cls) => (
                          <option key={cls} value={cls}>
                            {getClassDisplayName(cls)}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="GI Pipe">
                        {["EL", "L2", "M", "H"].map((cls) => (
                          <option key={cls} value={cls}>
                            {getClassDisplayName(cls)}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="HDPE Pipe">
                        {["PN-06", "PN-08", "PN-10", "PN-12.5", "PN-16"].map((cls) => (
                          <option key={cls} value={cls}>
                            {getClassDisplayName(cls)}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  </label>
                </div>
                <div>
                  <label style={labelStyle}>
                    Diameter:
                    {suctionPipeClass && (
                      <span style={{ fontSize: "12px", color: "#6c757d", fontWeight: "400", marginLeft: "8px" }}>
                        {getMaterialIcon(suctionPipeClass)} filtered for {suctionPipeClass} ({getMaterialDisplayName(suctionPipeClass)})
                      </span>
                    )}
                    <select
                      style={{
                        ...selectStyle,
                        borderColor: suctionClassError ? "#ff4d4f" : undefined,
                      }}
                      value={(suctionPipeSize || "").replace(/ /g, '_')}
                      onChange={(e) => {

                        const decodedValue = e.target.value.replace(/_/g, ' ');
                        setSuctionPipeSize(decodedValue);
                        setSuctionClassError(false);
                        setSuctionErrorMessage("");
                      }}
                      disabled={!suctionPipeClass}
                    >
                      <option value="">
                        {suctionPipeClass ? "Select pipe size" : "Select pipe class first"}
                      </option>
                      {suctionFilteredSizes.map((size) => {
                        const material = getMaterialFromClass(suctionPipeClass);
                        const displaySize = getDisplaySizeForHDPE(size, material);

                        const sizeValue = typeof size === 'object' ? size.inch.replace(/ /g, '_') : size.replace(/ /g, '_');
                        const sizeKey = typeof size === 'object' ? size.inch.replace(/ /g, '_') : size.replace(/ /g, '_');

                        return (
                          <option key={sizeKey} value={sizeValue}>
                            {displaySize}
                          </option>
                        );
                      })}
                    </select>
                  </label>
                </div>
                <div>
                  <label style={labelStyle}>
                    Length (m):
                    <input
                      type="number"
                      step="0.1"
                      value={suctionPipeLength}
                      onChange={(e) => setSuctionPipeLength(e.target.value)}
                      style={inputStyle}
                    />
                  </label>
                </div>
                <div>
                  <label style={labelStyle}>
                    Suction Head (m):
                    <input
                      type="number"
                      step="0.1"
                      value={suctionHead}
                      onChange={(e) => setSuctionHead(e.target.value)}
                      style={inputStyle}
                    />
                  </label>
                </div>
              </div>
            </div>
          )}

          {}
          <div style={cardStyle}>
            <div style={cardHeaderStyle}>Delivery Pipe</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
              <div>
                <label style={labelStyle}>
                  Pressure Class:
                  <select
                    style={{
                      ...selectStyle,
                      borderColor: deliveryClassError ? "#ff4d4f" : undefined,
                    }}
                    value={deliveryPipeClass}
                    onChange={(e) => {
                      setDeliveryPipeClass(e.target.value);
                      setDeliveryClassError(false);
                      setDeliveryErrorMessage("");
                    }}
                  >
                    <option value="">Select pipe class</option>
                    <optgroup label="uPVC Pipe">
                      {["CL6", "CL9", "CL12", "CL15"].map((cls) => (
                        <option key={cls} value={cls}>
                          {getClassDisplayName(cls)}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="GI Pipe">
                      {["EL", "L2", "M", "H"].map((cls) => (
                        <option key={cls} value={cls}>
                          {getClassDisplayName(cls)}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="HDPE Pipe">
                      {["PN-06", "PN-08", "PN-10", "PN-12.5", "PN-16"].map((cls) => (
                        <option key={cls} value={cls}>
                          {getClassDisplayName(cls)}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </label>
              </div>
              <div>
                <label style={labelStyle}>
                  Diameter:
                  {deliveryPipeClass && (
                    <span style={{ fontSize: "12px", color: "#6c757d", fontWeight: "400", marginLeft: "8px" }}>
                      {getMaterialIcon(deliveryPipeClass)} filtered for {deliveryPipeClass} ({getMaterialDisplayName(deliveryPipeClass)})
                    </span>
                  )}
                  <select
                    style={{
                      ...selectStyle,
                      borderColor: deliveryClassError ? "#ff4d4f" : undefined,
                    }}
                    value={(deliveryPipeSize || "").replace(/ /g, '_')}
                    onChange={(e) => {

                      const decodedValue = e.target.value.replace(/_/g, ' ');
                      setDeliveryPipeSize(decodedValue);
                      setDeliveryClassError(false);
                      setDeliveryErrorMessage("");
                    }}
                    disabled={!deliveryPipeClass}
                  >
                    <option value="">
                      {deliveryPipeClass ? "Select pipe size" : "Select pipe class first"}
                    </option>
                    {deliveryFilteredSizes.map((size) => {
                      const material = getMaterialFromClass(deliveryPipeClass);
                      const displaySize = getDisplaySizeForHDPE(size, material);

                      const sizeValue = typeof size === 'object' ? size.inch.replace(/ /g, '_') : size.replace(/ /g, '_');
                      const sizeKey = typeof size === 'object' ? size.inch.replace(/ /g, '_') : size.replace(/ /g, '_');

                      return (
                        <option key={sizeKey} value={sizeValue}>
                          {displaySize}
                        </option>
                      );
                    })}
                  </select>
                </label>
              </div>
              <div>
                <label style={labelStyle}>
                  Length (m):
                  <input
                    type="number"
                    step="0.1"
                    value={deliveryPipeLength}
                    onChange={(e) => setDeliveryPipeLength(e.target.value)}
                    style={inputStyle}
                  />
                </label>
              </div>
              <div>
                <label style={labelStyle}>
                  Delivery Head (m):
                  <input
                    type="number"
                    step="0.1"
                    value={deliveryHead}
                    onChange={(e) => setDeliveryHead(e.target.value)}
                    style={inputStyle}
                  />
                </label>
              </div>
            </div>
          </div>
        </div>

        {}
        <div style={{ textAlign: "center", marginBottom: "20px" }}>
          <button
            onClick={calculatePumpSpecs}
            disabled={pumpLoading || !deliveryPipeSize || !deliveryPipeClass || (selectedPump === "surface" && (!suctionPipeSize || !suctionPipeClass))}
            style={{
              padding: "12px 24px",
              backgroundColor: pumpLoading || !deliveryPipeSize || !deliveryPipeClass || (selectedPump === "surface" && (!suctionPipeSize || !suctionPipeClass)) ? "#ccc" : "#2196F3",
              color: "white",
              border: "none",
              borderRadius: "6px",
              fontSize: "16px",
              fontWeight: "500",
              cursor: pumpLoading || !deliveryPipeSize || !deliveryPipeClass || (selectedPump === "surface" && (!suctionPipeSize || !suctionPipeClass)) ? "not-allowed" : "pointer",
              transition: "background-color 0.3s",
            }}
          >
            {pumpLoading ? "Calculating..." : "Calculate Pump TDH"}
          </button>
        </div>

        {}
        {pumpCalculationResults && (
          <div style={{
            padding: "15px",
            backgroundColor: "#e3f2fd",
            borderRadius: "5px",
            border: "2px solid #2196F3",
            marginBottom: "20px",
          }}>
            <h5 style={{ margin: "0 0 10px 0", color: "#1976d2" }}>
              Pump TDH Calculation Results:
            </h5>
            <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: "10px",
              fontSize: "14px",
            }}>
              <div>
                <strong>Friction Losses:</strong><br />
                {(() => {
                  let suctionLoss = 0;
                  if (selectedPump === "surface" && pumpCalculationResults.suction_pipe) {
                    suctionLoss = parseFloat(pumpCalculationResults.suction_pipe.friction_losses_m) || 0;
                  }
                  const deliveryLoss = parseFloat(pumpCalculationResults.delivery_pipe.friction_losses_m) || 0;
                  return (suctionLoss + deliveryLoss).toFixed(2);
                })()}m
              </div>
              <div>
                <strong>Minor Losses:</strong><br />
                {(() => {
                  let suctionLoss = 0;
                  if (selectedPump === "surface" && pumpCalculationResults.suction_pipe) {
                    suctionLoss = parseFloat(pumpCalculationResults.suction_pipe.friction_losses_m) || 0;
                  }
                  const deliveryLoss = parseFloat(pumpCalculationResults.delivery_pipe.friction_losses_m) || 0;
                  const frictionLosses = suctionLoss + deliveryLoss;
                  return (frictionLosses * 0.2).toFixed(2);
                })()}m
              </div>
              <div>
                <strong>Static Head:</strong><br />
                {(() => {
                  let totalHead = 0;
                  if (selectedPump === "surface") {
                    totalHead = (parseFloat(suctionHead) || 0) + (parseFloat(deliveryHead) || 0);
                  } else {
                    totalHead = parseFloat(deliveryHead) || 0;
                  }
                  return totalHead.toFixed(2);
                })()}m
              </div>
              <div style={{
                gridColumn: "1 / -1",
                textAlign: "center",
                fontSize: "16px",
                fontWeight: "bold",
                color: "#1976d2",
                marginTop: "10px",
                padding: "10px",
                backgroundColor: "#bbdefb",
                borderRadius: "4px",
              }}>
                Total Pump TDH: {calculatePumpTDH().toFixed(2)}m
              </div>
            </div>
          </div>
        )}

        {}
        {pumpError && <p style={{ color: "#ff4d4f", fontSize: "14px" }}>{pumpError}</p>}
        {suctionErrorMessage && <p style={{ color: "#ff4d4f", fontSize: "14px" }}>{suctionErrorMessage}</p>}
        {deliveryErrorMessage && <p style={{ color: "#ff4d4f", fontSize: "14px" }}>{deliveryErrorMessage}</p>}
      </div>

      {}
      <div style={sectionStyle}>
        <div style={radioGroupStyle}>
          {}
          <div
            style={
              selectedOption === "drip-lateral"
                ? radioOptionActiveStyle
                : radioOptionStyle
            }
            onClick={() => handleOptionChange("drip-lateral")}
          >
            <input
              type="radio"
              id="drip-lateral"
              name="irrigationSystem"
              value="drip-lateral"
              checked={selectedOption === "drip-lateral"}
              onChange={() => handleOptionChange("drip-lateral")}
              style={radioInputStyle}
            />
            <div>
              <label htmlFor="drip-lateral" style={labelStyle}>
                Drip Lateral (on line)
              </label>
              <div style={descriptionStyle}>
                Traditional drip irrigation system with lateral lines and
                on-line emitters
              </div>
            </div>
          </div>

          {}
          <div
            style={
              selectedOption === "drip-line"
                ? radioOptionActiveStyle
                : radioOptionStyle
            }
            onClick={() => handleOptionChange("drip-line")}
          >
            <input
              type="radio"
              id="drip-line"
              name="irrigationSystem"
              value="drip-line"
              checked={selectedOption === "drip-line"}
              onChange={() => handleOptionChange("drip-line")}
              style={radioInputStyle}
            />
            <div>
              <label htmlFor="drip-line" style={labelStyle}>
                Drip Line (Integrated)
              </label>
              <div style={descriptionStyle}>
                Integrated drip line system with built-in emitters for uniform
                water distribution
              </div>
            </div>
          </div>
        </div>
      </div>

      {}
      <>
        {selectedOption === "drip-lateral" && (
          <div>
            {}
            <div style={sectionStyle}>
              <h4 style={{ marginBottom: "15px", color: "#555" }}>Lateral</h4>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "15px",
                }}
              >
                <div>
                  <label
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Longest Lateral Length (m):
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.lateralLength}
                    onChange={(e) =>
                      handleInputChange("lateralLength", e.target.value)
                    }
                    style={{
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "100%",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Lateral Outer Dia (mm):
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.lateralOuterDia}
                    onChange={(e) =>
                      handleInputChange("lateralOuterDia", e.target.value)
                    }
                    style={{
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "100%",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Lateral Thickness (mm):
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.lateralThickness}
                    onChange={(e) =>
                      handleInputChange("lateralThickness", e.target.value)
                    }
                    style={{
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "100%",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Lateral Inner Dia (mm):
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.lateralInnerDia}
                    readOnly
                    style={{
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "100%",
                      backgroundColor: "#f5f5f5",
                      color: "#666",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    No. of Plant per Lateral Length:
                  </label>
                  <input
                    type="number"
                    value={formData.noOfPlantsPerLateral}
                    onChange={(e) =>
                      handleInputChange("noOfPlantsPerLateral", e.target.value)
                    }
                    style={{
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "100%",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Drippers per plant per Lateral:
                  </label>
                  <input
                    type="number"
                    value={formData.drippersPerPlant}
                    onChange={(e) =>
                      handleInputChange("drippersPerPlant", e.target.value)
                    }
                    style={{
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "100%",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Flow per dripper (LPH):
                  </label>
                  <input
                    type="number"
                    value={formData.flowPerDripper}
                    onChange={(e) =>
                      handleInputChange("flowPerDripper", e.target.value)
                    }
                    style={{
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "100%",
                    }}
                  />
                </div>
              </div>

              {}
              <div
                style={{
                  marginTop: "15px",
                  padding: "10px",
                  backgroundColor: "#f0f8ff",
                  borderRadius: "5px",
                  border: "1px solid #2196F3",
                }}
              >
                <h5 style={{ margin: "0 0 10px 0", color: "#2196F3" }}>
                  Calculated Values:
                </h5>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: "10px",
                    fontSize: "14px",
                  }}
                >
                  <div>
                    <strong>Total Flow (LPH):</strong>
                    <br />
                    {formData.totalFlowPerLateral || "0"}
                  </div>
                  <div>
                    <strong>Head Loss (m):</strong>
                    <br />
                    {calculatedValues.lateralHeadLoss?.toFixed(2) || "0.00"}
                  </div>
                  <div>
                    <strong>Outlet Factor:</strong>
                    <br />
                    {calculatedValues.lateralOutletFactor?.toFixed(3) ||
                      "0.000"}
                  </div>
                  <div>
                    <strong>Net Head Loss (m):</strong>
                    <br />
                    <span
                      style={{
                        color:
                          calculatedValues.lateralNetHeadLoss > 2
                            ? "#f44336"
                            : "#4CAF50",
                      }}
                    >
                      {calculatedValues.lateralNetHeadLoss?.toFixed(2) ||
                        "0.00"}
                    </span>
                    {calculatedValues.lateralNetHeadLoss > 2 && (
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#d32f2f",
                          marginTop: "4px",
                          padding: "6px 8px",
                          backgroundColor: "#ffebee",
                          border: "1px solid #f44336",
                          borderRadius: "4px",
                          fontWeight: "500",
                        }}
                      >
                        ⚠️ Must not be more than 2 m, plz reduce lateral length
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {}
            {subMains.map((subMainNumber, index) => (
              <div key={subMainNumber} style={sectionStyle}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "15px",
                  }}
                >
                  <h4 style={{ color: "#555", margin: 0 }}>
                    Sub Main {subMainNumber}
                  </h4>
                  <div>
                    {index === subMains.length - 1 && (
                      <button
                        type="button"
                        onClick={addSubMain}
                        style={{
                          padding: "5px 10px",
                          backgroundColor: "#4CAF50",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          marginRight: "5px",
                          cursor: "pointer",
                        }}
                      >
                        + Add Sub Main
                      </button>
                    )}
                    {subMains.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSubMain(subMainNumber)}
                        style={{
                          padding: "5px 10px",
                          backgroundColor: "#f44336",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "15px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        fontWeight: "500",
                        display: "block",
                        marginBottom: "5px",
                      }}
                    >
                      Pressure Class:
                    </label>
                    <select
                      style={{
                        padding: "8px",
                        border: `1px solid ${
                          subMainClassErrors[subMainNumber] ? "#ff4d4f" : "#ddd"
                        }`,
                        borderRadius: "4px",
                        width: "100%",
                      }}
                      value={formData[`subMain${subMainNumber}Class`] || ""}
                      onChange={(e) => {
                        const newClass = e.target.value;
                        handleInputChange(
                          `subMain${subMainNumber}Class`,
                          newClass
                        );
                        setSubMainClassErrors((prev) => ({
                          ...prev,
                          [subMainNumber]: false,
                        }));
                        setSubMainErrorMessages((prev) => ({
                          ...prev,
                          [subMainNumber]: "",
                        }));
                      }}
                    >
                      <option value="">Select pipe class</option>

                      <optgroup label="uPVC Pipe">
                        {pipeClasses
                          .filter((cls) =>
                            ["CL6", "CL9", "CL12", "CL15"].includes(cls)
                          )
                          .map((cls) => (
                            <option key={cls} value={cls}>
                              {getClassDisplayName(cls)}
                            </option>
                          ))}
                      </optgroup>

                      <optgroup label="GI Pipe">
                        {pipeClasses
                          .filter((cls) => ["EL", "L2", "M", "H"].includes(cls))
                          .map((cls) => (
                            <option key={cls} value={cls}>
                              {getClassDisplayName(cls)}
                            </option>
                          ))}
                      </optgroup>

                      <optgroup label="HDPE Pipe">
                        {pipeClasses
                          .filter((cls) =>
                            [
                              "PN-06",
                              "PN-08",
                              "PN-10",
                              "PN-12.5",
                              "PN-16",
                            ].includes(cls)
                          )
                          .map((cls) => (
                            <option key={cls} value={cls}>
                              {getClassDisplayName(cls)}
                            </option>
                          ))}
                      </optgroup>
                    </select>
                    {subMainClassErrors[subMainNumber] && (
                      <div
                        style={{
                          color: "#ff4d4f",
                          fontSize: "12px",
                          marginTop: "4px",
                        }}
                      >
                        {subMainErrorMessages[subMainNumber]}
                      </div>
                    )}
                  </div>
                  <div>
                    <label
                      style={{
                        fontWeight: "500",
                        display: "block",
                        marginBottom: "5px",
                      }}
                    >
                      Sub Main Outer Diameter:
                      {formData[`subMain${subMainNumber}Class`] && (
                        <span
                          style={{
                            fontSize: "12px",
                            color: "#6c757d",
                            fontWeight: "400",
                            marginLeft: "8px",
                          }}
                        >
                          {getMaterialIcon(
                            formData[`subMain${subMainNumber}Class`]
                          )}{" "}
                          filtered for{" "}
                          {formData[`subMain${subMainNumber}Class`]} (
                          {getMaterialDisplayName(
                            formData[`subMain${subMainNumber}Class`]
                          )}
                          )
                        </span>
                      )}
                    </label>
                    <select
                      style={{
                        padding: "8px",
                        border: `1px solid ${
                          subMainClassErrors[subMainNumber] ? "#ff4d4f" : "#ddd"
                        }`,
                        borderRadius: "4px",
                        width: "100%",
                      }}
                      value={(formData[`subMain${subMainNumber}Size`] || "").replace(/ /g, '_')}
                      onChange={(e) => {

                        const selectedSize = e.target.value.replace(/_/g, ' ');

                        let decimalValue = selectedSize;
                        if (selectedSize.includes("/")) {

                          if (selectedSize === "1/2") decimalValue = "0.5";
                          else if (selectedSize === "3/4") decimalValue = "0.75";
                          else if (selectedSize === "1 1/4") decimalValue = "1.25";
                          else if (selectedSize === "1 1/2") decimalValue = "1.5";
                          else if (selectedSize === "2 1/2") decimalValue = "2.5";

                          else if (selectedSize === "4/5") decimalValue = "0.8";
                          else if (selectedSize === "1 4/7") decimalValue = "1.571";
                          else if (selectedSize === "3 1/2") decimalValue = "3.5";
                          else if (selectedSize === "4 1/3") decimalValue = "4.333";
                          else if (selectedSize === "5 1/2") decimalValue = "5.5";
                          else if (selectedSize === "6 2/7") decimalValue = "6.285714286";
                          else {

                            try {
                              const parts = selectedSize.split(' ');
                              if (parts.length === 2) {

                                const wholePart = parseInt(parts[0]);
                                const fractionParts = parts[1].split('/');
                                if (fractionParts.length === 2) {
                                  const numerator = parseInt(fractionParts[0]);
                                  const denominator = parseInt(fractionParts[1]);
                                  decimalValue = (wholePart + numerator / denominator).toString();
                                }
                              } else if (parts.length === 1 && selectedSize.includes('/')) {

                                const fractionParts = selectedSize.split('/');
                                if (fractionParts.length === 2) {
                                  const numerator = parseInt(fractionParts[0]);
                                  const denominator = parseInt(fractionParts[1]);
                                  decimalValue = (numerator / denominator).toString();
                                }
                              }
                            } catch (e) {
                              decimalValue = parseFloat(selectedSize) || selectedSize;
                            }
                          }
                        }

                        handleInputChange(
                          `subMain${subMainNumber}Size`,
                          selectedSize
                        );
                        handleInputChange(
                          `subMain${subMainNumber}OuterDia`,
                          decimalValue
                        );
                        setSubMainClassErrors((prev) => ({
                          ...prev,
                          [subMainNumber]: false,
                        }));
                        setSubMainErrorMessages((prev) => ({
                          ...prev,
                          [subMainNumber]: "",
                        }));
                      }}
                      disabled={!formData[`subMain${subMainNumber}Class`]}
                    >
                      <option value="">
                        {formData[`subMain${subMainNumber}Class`]
                          ? "Select pipe size"
                          : "Select pipe class first"}
                      </option>
                      {formData[`subMain${subMainNumber}Class`] &&
                      (!subMainFilteredSizes[subMainNumber] ||
                        subMainFilteredSizes[subMainNumber].length === 0) ? (
                        <option value="" disabled>
                          No sizes available for{" "}
                          {formData[`subMain${subMainNumber}Class`]}
                        </option>
                      ) : (
                        (subMainFilteredSizes[subMainNumber] || []).map(
                          (size) => {
                            const material = getMaterialFromClass(
                              formData[`subMain${subMainNumber}Class`]
                            );
                            const displaySize = getDisplaySizeForHDPE(
                              size,
                              material
                            );

                            const sizeValue = typeof size === 'object' ? size.inch.replace(/ /g, '_') : size.replace(/ /g, '_');
                            const sizeKey = typeof size === 'object' ? size.inch.replace(/ /g, '_') : size.replace(/ /g, '_');

                            return (
                              <option key={sizeKey} value={sizeValue}>
                                {displaySize}
                              </option>
                            );
                          }
                        )
                      )}
                    </select>
                    {!formData[`subMain${subMainNumber}Class`] && (
                      <div
                        style={{
                          marginTop: "6px",
                          fontSize: "11px",
                          color: "#6c757d",
                          fontStyle: "italic",
                        }}
                      >
                        💡 Select a pipe class above to see available sizes
                      </div>
                    )}
                    {formData[`subMain${subMainNumber}Class`] &&
                      (!subMainFilteredSizes[subMainNumber] ||
                        subMainFilteredSizes[subMainNumber].length === 0) && (
                        <div
                          style={{
                            marginTop: "6px",
                            padding: "8px 12px",
                            backgroundColor: "#fff3cd",
                            border: "1px solid #ffeaa7",
                            borderRadius: "4px",
                            fontSize: "12px",
                            color: "#856404",
                          }}
                        >
                          ⚠️ No pipe sizes available for class{" "}
                          {formData[`subMain${subMainNumber}Class`]}. Try
                          selecting a different class.
                        </div>
                      )}
                  </div>
                  <div>
                    <label
                      style={{
                        fontWeight: "500",
                        display: "block",
                        marginBottom: "5px",
                      }}
                    >
                      Sub Main Length (m):
                    </label>
                    <input
                      type="number"
                      value={formData[`subMain${subMainNumber}Length`] || ""}
                      onChange={(e) =>
                        handleInputChange(
                          `subMain${subMainNumber}Length`,
                          e.target.value
                        )
                      }
                      style={{
                        padding: "8px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        width: "100%",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        fontWeight: "500",
                        display: "block",
                        marginBottom: "5px",
                      }}
                    >
                      Sub Main Inner Dia (mm):
                      <span
                        style={{
                          fontSize: "12px",
                          color: "#666",
                          fontWeight: "normal",
                        }}
                      >
                        {" "}
                        (Auto-calculated)
                      </span>
                    </label>
                    <input
                      type="text"
                      value={
                        formData[`subMain${subMainNumber}InnerDia`] ||
                        "Auto-calculated"
                      }
                      readOnly
                      style={{
                        padding: "8px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        width: "100%",
                        backgroundColor: formData[
                          `subMain${subMainNumber}InnerDia`
                        ]?.startsWith("Error:")
                          ? "#ffe6e6"
                          : "#f5f5f5",
                        color: formData[
                          `subMain${subMainNumber}InnerDia`
                        ]?.startsWith("Error:")
                          ? "#d32f2f"
                          : "#666",
                        fontWeight: formData[
                          `subMain${subMainNumber}InnerDia`
                        ]?.startsWith("Error:")
                          ? "500"
                          : "normal",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        fontWeight: "500",
                        display: "block",
                        marginBottom: "5px",
                      }}
                    >
                      No. of Lateral/ Only Sub Main{subMainNumber}:
                    </label>
                    <input
                      type="number"
                      value={
                        formData[`subMain${subMainNumber}NoOfLateralOnly`] || ""
                      }
                      onChange={(e) =>
                        handleInputChange(
                          `subMain${subMainNumber}NoOfLateralOnly`,
                          e.target.value
                        )
                      }
                      style={{
                        padding: "8px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        width: "100%",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        fontWeight: "500",
                        display: "block",
                        marginBottom: "5px",
                      }}
                    >
                      Flow per Lateral (LPH):
                    </label>
                    <input
                      type="number"
                      value={
                        formData[`subMain${subMainNumber}FlowPerLateral`] ||
                        (() => {
                          if (selectedOption === "drip-lateral") {
                            return formData.totalFlowPerLateral || "";
                          } else if (selectedOption === "drip-line") {
                            return formData.dripLineTotalFlow || "";
                          }
                          return "";
                        })()
                      }
                      onChange={(e) =>
                        handleInputChange(
                          `subMain${subMainNumber}FlowPerLateral`,
                          e.target.value
                        )
                      }
                      placeholder={(() => {
                        if (selectedOption === "drip-lateral") {
                          return formData.totalFlowPerLateral
                            ? `Auto: ${parseFloat(
                                formData.totalFlowPerLateral
                              ).toFixed(2)}`
                            : "Auto-calculated";
                        } else if (selectedOption === "drip-line") {
                          return formData.dripLineTotalFlow
                            ? `Auto: ${parseFloat(
                                formData.dripLineTotalFlow
                              ).toFixed(2)}`
                            : "Auto-calculated";
                        }
                        return "Auto-calculated";
                      })()}
                      style={{
                        padding: "8px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        width: "100%",
                      }}
                    />
                  </div>
                </div>

                {}
                <div
                  style={{
                    marginTop: "15px",
                    padding: "10px",
                    backgroundColor: "#f0f8ff",
                    borderRadius: "5px",
                    border: "1px solid #2196F3",
                  }}
                >
                  <h6 style={{ margin: "0 0 10px 0", color: "#2196F3" }}>
                    Sub Main {subMainNumber} Calculated Values:
                  </h6>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gap: "10px",
                      fontSize: "14px",
                    }}
                  >
                    <div>
                      <strong>Total Flow (LPH):</strong>
                      <br />
                      {calculatedValues.subMainTotalFlow[
                        subMainNumber
                      ]?.toFixed(2) || "0.00"}
                    </div>
                    <div>
                      <strong>Head Loss (m):</strong>
                      <br />
                      {calculatedValues.subMainHeadLoss[subMainNumber]?.toFixed(
                        2
                      ) || "0.00"}
                    </div>
                    <div>
                      <strong>Outlet Factor:</strong>
                      <br />
                      {calculatedValues.subMainOutletFactor[
                        subMainNumber
                      ]?.toFixed(3) || "0.000"}
                    </div>
                    <div>
                      <strong>Net Head Loss (m):</strong>
                      <br />
                      {calculatedValues.subMainNetHeadLoss[
                        subMainNumber
                      ]?.toFixed(2) || "0.00"}
                    </div>
                    <div>
                      <strong>Velocity (m/s):</strong>
                      <br />
                      {calculatedValues.subMainVelocity[
                        subMainNumber
                      ]?.toFixed(2) || "0.00"}
                      {calculatedValues.subMainVelocity[subMainNumber] > 1.5 && (
                        <div style={{ color: "red", fontSize: "12px", marginTop: "5px" }}>
                          Caution: Velocity is greater than 1.5m/s, consider readjustment
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {}
            <div style={sectionStyle}>
              <h4 style={{ marginBottom: "15px", color: "#555" }}>Main</h4>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "15px",
                }}
              >
                <div>
                  <label
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Pipe Class:
                  </label>
                  <select
                    value={formData.mainClass}
                    onChange={(e) =>
                      handleInputChange("mainClass", e.target.value)
                    }
                    style={{
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "100%",
                      backgroundColor: "white",
                    }}
                  >
                    <option value="">Select pipe class</option>

                    <optgroup label="uPVC Pipe">
                      {pipeClasses
                        .filter((cls) =>
                          ["CL6", "CL9", "CL12", "CL15"].includes(cls)
                        )
                        .map((cls) => (
                          <option key={cls} value={cls}>
                            {getClassDisplayName(cls)}
                          </option>
                        ))}
                    </optgroup>

                    <optgroup label="GI Pipe">
                      {pipeClasses
                        .filter((cls) => ["EL", "L2", "M", "H"].includes(cls))
                        .map((cls) => (
                          <option key={cls} value={cls}>
                            {getClassDisplayName(cls)}
                          </option>
                        ))}
                    </optgroup>

                    <optgroup label="HDPE Pipe">
                      {pipeClasses
                        .filter((cls) =>
                          [
                            "PN-06",
                            "PN-08",
                            "PN-10",
                            "PN-12.5",
                            "PN-16",
                          ].includes(cls)
                        )
                        .map((cls) => (
                          <option key={cls} value={cls}>
                            {getClassDisplayName(cls)}
                          </option>
                        ))}
                    </optgroup>
                  </select>
                  {mainClassError && (
                    <div
                      style={{
                        color: "#ff4d4f",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {mainErrorMessage}
                    </div>
                  )}
                </div>
                <div>
                  <label
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Main Outer Diameter:
                    {formData.mainClass && (
                      <span
                        style={{
                          fontSize: "12px",
                          color: "#6c757d",
                          fontWeight: "400",
                          marginLeft: "8px",
                        }}
                      >
                        {getMaterialIcon(formData.mainClass)} filtered for{" "}
                        {formData.mainClass} (
                        {getMaterialDisplayName(formData.mainClass)})
                      </span>
                    )}
                  </label>
                  <select
                    value={(() => {
                      const encodedValue = (formData.mainSize || "").replace(/ /g, '_');
                      return encodedValue;
                    })()}
                    onChange={(e) => {

                      const selectedSize = e.target.value.replace(/_/g, ' ');

                      let decimalValue = selectedSize;
                      if (selectedSize.includes("/")) {

                        if (selectedSize === "1/2") decimalValue = "0.5";
                        else if (selectedSize === "3/4") decimalValue = "0.75";
                        else if (selectedSize === "1 1/4") decimalValue = "1.25";
                        else if (selectedSize === "1 1/2") decimalValue = "1.5";
                        else if (selectedSize === "2 1/2") decimalValue = "2.5";

                        else if (selectedSize === "4/5") decimalValue = "0.8";
                        else if (selectedSize === "1 4/7") decimalValue = "1.571";
                        else if (selectedSize === "3 1/2") decimalValue = "3.5";
                        else if (selectedSize === "4 1/3") decimalValue = "4.333";
                        else if (selectedSize === "5 1/2") decimalValue = "5.5";
                        else if (selectedSize === "6 2/7") decimalValue = "6.285714286";
                        else {

                          try {
                            const parts = selectedSize.split(' ');
                            if (parts.length === 2) {

                              const wholePart = parseInt(parts[0]);
                              const fractionParts = parts[1].split('/');
                              if (fractionParts.length === 2) {
                                const numerator = parseInt(fractionParts[0]);
                                const denominator = parseInt(fractionParts[1]);
                                decimalValue = (wholePart + numerator / denominator).toString();
                              }
                            } else if (parts.length === 1 && selectedSize.includes('/')) {

                              const fractionParts = selectedSize.split('/');
                              if (fractionParts.length === 2) {
                                const numerator = parseInt(fractionParts[0]);
                                const denominator = parseInt(fractionParts[1]);
                                decimalValue = (numerator / denominator).toString();
                              }
                            }
                          } catch (e) {
                            decimalValue = parseFloat(selectedSize) || selectedSize;
                          }
                        }
                      }

                      handleInputChange("mainSize", selectedSize);
                      handleInputChange("mainOuterDia", decimalValue);
                    }}
                    style={{
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "100%",
                      backgroundColor: "white",
                    }}
                  >
                    <option value="">Select pipe size</option>
                    {(mainFilteredSizes.length > 0
                      ? mainFilteredSizes
                      : allPipeSizes
                    ).map((size) => {
                      const material = getMaterialFromClass(formData.mainClass);
                      const displaySize = getDisplaySizeForHDPE(size, material);

                      const sizeValue = typeof size === 'object' ? size.inch.replace(/ /g, '_') : size.replace(/ /g, '_');
                      const sizeKey = typeof size === 'object' ? size.inch.replace(/ /g, '_') : size.replace(/ /g, '_');

                      return (
                        <option key={sizeKey} value={sizeValue}>
                          {displaySize}
                        </option>
                      );
                    })}
                  </select>
                  {!formData.mainClass && (
                    <div
                      style={{
                        marginTop: "6px",
                        fontSize: "11px",
                        color: "#6c757d",
                        fontStyle: "italic",
                      }}
                    >
                      💡 Select a pipe class above to see available sizes
                    </div>
                  )}
                  {formData.mainClass &&
                    (!mainFilteredSizes || mainFilteredSizes.length === 0) && (
                      <div
                        style={{
                          marginTop: "6px",
                          padding: "8px 12px",
                          backgroundColor: "#fff3cd",
                          border: "1px solid #ffeaa7",
                          borderRadius: "4px",
                          fontSize: "12px",
                          color: "#856404",
                        }}
                      >
                        ⚠️ No pipe sizes available for class{" "}
                        {formData.mainClass}. Try selecting a different class.
                      </div>
                    )}
                </div>
                <div>
                  <label
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Main Length (m):
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.mainLength}
                    onChange={(e) =>
                      handleInputChange("mainLength", e.target.value)
                    }
                    style={{
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "100%",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Main Inner Dia (mm):
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.mainInnerDia}
                    readOnly
                    style={{
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "100%",
                      backgroundColor: "#f5f5f5",
                      color: "#666",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Total Flow (LPH) on Main:
                  </label>
                  <input
                    type="number"
                    value={formData.totalFlowMain}
                    onChange={(e) =>
                      handleInputChange("totalFlowMain", e.target.value)
                    }
                    style={{
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "100%",
                    }}
                  />
                </div>
              </div>

              {}
              <div
                style={{
                  marginTop: "15px",
                  padding: "10px",
                  backgroundColor: "#f0f8ff",
                  borderRadius: "5px",
                  border: "1px solid #2196F3",
                }}
              >
                <h5 style={{ margin: "0 0 10px 0", color: "#2196F3" }}>
                  Main Pipe Calculated Values:
                </h5>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: "10px",
                    fontSize: "14px",
                  }}
                >
                  <div>
                    <strong>Total Flow (LPH):</strong>
                    <br />
                    {formData.totalFlowMain || "0"}
                  </div>
                  <div>
                    <strong>Head Loss (m):</strong>
                    <br />
                    {calculatedValues.mainHeadLoss?.toFixed(2) || "0.00"}
                  </div>
                  <div>
                    <strong>Net Head Loss (m):</strong>
                    <br />
                    {calculatedValues.mainNetHeadLoss?.toFixed(2) || "0.00"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {}
        {selectedOption === "drip-line" && (
          <div>
            {}
            <div style={sectionStyle}>
              <h4 style={{ marginBottom: "15px", color: "#555" }}>Lateral</h4>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "15px",
                }}
              >
                <div>
                  <label
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Longest Lateral Length (m):
                  </label>
                  <input
                    type="number"
                    value={formData.dripLineLength}
                    onChange={(e) =>
                      handleInputChange("dripLineLength", e.target.value)
                    }
                    style={{
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "100%",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Lateral Outer Dia (mm):
                  </label>
                  <input
                    type="number"
                    value={formData.dripLineOuterDia}
                    onChange={(e) =>
                      handleInputChange("dripLineOuterDia", e.target.value)
                    }
                    style={{
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "100%",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Lateral Thickness (mm):
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.dripLineThickness}
                    onChange={(e) =>
                      handleInputChange("dripLineThickness", e.target.value)
                    }
                    style={{
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "100%",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Lateral Inner Dia (mm):
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.dripLineInnerDia}
                    readOnly
                    style={{
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "100%",
                      backgroundColor: "#f5f5f5",
                      color: "#666",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Dripper Spacing (cm):
                  </label>
                  <input
                    type="number"
                    value={formData.dripperSpacing}
                    onChange={(e) =>
                      handleInputChange("dripperSpacing", e.target.value)
                    }
                    style={{
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "100%",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Drippers per Lateral:
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        fontWeight: "normal",
                      }}
                    >
                      {" "}
                      (Auto-calculated)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={formData.drippersPerLateral || "Auto-calculated"}
                    readOnly
                    style={{
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "100%",
                      backgroundColor: "#f5f5f5",
                      color: "#666",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Flow per dripper (LPH):
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.dripLineFlowPerDripper}
                    onChange={(e) =>
                      handleInputChange(
                        "dripLineFlowPerDripper",
                        e.target.value
                      )
                    }
                    style={{
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "100%",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Total Flow (LPH) per Lateral:
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        fontWeight: "normal",
                      }}
                    >
                      {" "}
                      (Auto-calculated)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={
                      formData.dripLineTotalFlow
                        ? parseFloat(formData.dripLineTotalFlow).toFixed(2)
                        : "Auto-calculated"
                    }
                    readOnly
                    style={{
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "100%",
                      backgroundColor: "#f5f5f5",
                      color: "#666",
                    }}
                  />
                </div>
              </div>

              {}
              <div
                style={{
                  marginTop: "15px",
                  padding: "10px",
                  backgroundColor: "#f0f8ff",
                  borderRadius: "5px",
                  border: "1px solid #2196F3",
                }}
              >
                <h5 style={{ margin: "0 0 10px 0", color: "#2196F3" }}>
                  Calculated Values:
                </h5>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: "10px",
                    fontSize: "14px",
                  }}
                >
                  <div>
                    <strong>Drippers per Lateral:</strong>
                    <br />
                    {formData.drippersPerLateral || "0"}
                  </div>
                  <div>
                    <strong>Total Flow (LPH):</strong>
                    <br />
                    {parseFloat(formData.dripLineTotalFlow || 0).toFixed(2)}
                  </div>
                  <div>
                    <strong>Head Loss (m):</strong>
                    <br />
                    {calculatedValues.dripLineHeadLoss?.toFixed(2) || "0.00"}
                  </div>
                  <div>
                    <strong>Outlet Factor:</strong>
                    <br />
                    {calculatedValues.dripLineOutletFactor?.toFixed(3) ||
                      "0.000"}
                  </div>
                  <div>
                    <strong>Net Head Loss (m):</strong>
                    <br />
                    <span
                      style={{
                        color:
                          calculatedValues.dripLineNetHeadLoss > 2
                            ? "#f44336"
                            : "#4CAF50",
                      }}
                    >
                      {calculatedValues.dripLineNetHeadLoss?.toFixed(2) ||
                        "0.00"}
                    </span>
                    {calculatedValues.dripLineNetHeadLoss > 2 && (
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#d32f2f",
                          marginTop: "4px",
                          padding: "6px 8px",
                          backgroundColor: "#ffebee",
                          border: "1px solid #f44336",
                          borderRadius: "4px",
                          fontWeight: "500",
                        }}
                      >
                        ⚠️ Must not be more than 2 m, plz reduce lateral length
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {}
            {subMains.map((subMainNumber, index) => (
              <div key={subMainNumber} style={sectionStyle}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "15px",
                  }}
                >
                  <h4 style={{ color: "#555", margin: 0 }}>
                    Sub Main {subMainNumber}
                  </h4>
                  <div>
                    {index === subMains.length - 1 && (
                      <button
                        type="button"
                        onClick={addSubMain}
                        style={{
                          padding: "5px 10px",
                          backgroundColor: "#4CAF50",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          marginRight: "5px",
                          cursor: "pointer",
                        }}
                      >
                        + Add Sub Main
                      </button>
                    )}
                    {subMains.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSubMain(subMainNumber)}
                        style={{
                          padding: "5px 10px",
                          backgroundColor: "#f44336",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "15px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        fontWeight: "500",
                        display: "block",
                        marginBottom: "5px",
                      }}
                    >
                      Sub Main Length (m):
                    </label>
                    <input
                      type="number"
                      value={formData[`subMain${subMainNumber}Length`] || ""}
                      onChange={(e) =>
                        handleInputChange(
                          `subMain${subMainNumber}Length`,
                          e.target.value
                        )
                      }
                      style={{
                        padding: "8px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        width: "100%",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        fontWeight: "500",
                        display: "block",
                        marginBottom: "5px",
                      }}
                    >
                      Pressure Class:
                    </label>
                    <select
                      style={{
                        padding: "8px",
                        border: `1px solid ${
                          subMainClassErrors[subMainNumber] ? "#ff4d4f" : "#ddd"
                        }`,
                        borderRadius: "4px",
                        width: "100%",
                      }}
                      value={formData[`subMain${subMainNumber}Class`] || ""}
                      onChange={(e) => {
                        const newClass = e.target.value;
                        handleInputChange(
                          `subMain${subMainNumber}Class`,
                          newClass
                        );
                        setSubMainClassErrors((prev) => ({
                          ...prev,
                          [subMainNumber]: false,
                        }));
                        setSubMainErrorMessages((prev) => ({
                          ...prev,
                          [subMainNumber]: "",
                        }));
                      }}
                    >
                      <option value="">Select pipe class</option>

                      <optgroup label="uPVC Pipe">
                        {pipeClasses
                          .filter((cls) =>
                            ["CL6", "CL9", "CL12", "CL15"].includes(cls)
                          )
                          .map((cls) => (
                            <option key={cls} value={cls}>
                              {getClassDisplayName(cls)}
                            </option>
                          ))}
                      </optgroup>

                      <optgroup label="GI Pipe">
                        {pipeClasses
                          .filter((cls) => ["EL", "L2", "M", "H"].includes(cls))
                          .map((cls) => (
                            <option key={cls} value={cls}>
                              {getClassDisplayName(cls)}
                            </option>
                          ))}
                      </optgroup>

                      <optgroup label="HDPE Pipe">
                        {pipeClasses
                          .filter((cls) =>
                            [
                              "PN-06",
                              "PN-08",
                              "PN-10",
                              "PN-12.5",
                              "PN-16",
                            ].includes(cls)
                          )
                          .map((cls) => (
                            <option key={cls} value={cls}>
                              {getClassDisplayName(cls)}
                            </option>
                          ))}
                      </optgroup>
                    </select>
                    {subMainClassErrors[subMainNumber] && (
                      <div
                        style={{
                          color: "#ff4d4f",
                          fontSize: "12px",
                          marginTop: "4px",
                        }}
                      >
                        {subMainErrorMessages[subMainNumber]}
                      </div>
                    )}
                  </div>
                  <div>
                    <label
                      style={{
                        fontWeight: "500",
                        display: "block",
                        marginBottom: "5px",
                      }}
                    >
                      Sub Main Outer Diameter:
                      {formData[`subMain${subMainNumber}Class`] && (
                        <span
                          style={{
                            fontSize: "12px",
                            color: "#6c757d",
                            fontWeight: "400",
                            marginLeft: "8px",
                          }}
                        >
                          {getMaterialIcon(
                            formData[`subMain${subMainNumber}Class`]
                          )}{" "}
                          filtered for{" "}
                          {formData[`subMain${subMainNumber}Class`]} (
                          {getMaterialDisplayName(
                            formData[`subMain${subMainNumber}Class`]
                          )}
                          )
                        </span>
                      )}
                    </label>
                    <select
                      style={{
                        padding: "8px",
                        border: `1px solid ${
                          subMainClassErrors[subMainNumber] ? "#ff4d4f" : "#ddd"
                        }`,
                        borderRadius: "4px",
                        width: "100%",
                      }}
                      value={(formData[`subMain${subMainNumber}Size`] || "").replace(/ /g, '_')}
                      onChange={(e) => {

                        const selectedSize = e.target.value.replace(/_/g, ' ');

                        handleInputChange(
                          `subMain${subMainNumber}Size`,
                          selectedSize
                        );

                        let decimalValue = selectedSize;
                        if (selectedSize.includes('/')) {

                          if (selectedSize === "1/2") decimalValue = "0.5";
                          else if (selectedSize === "3/4") decimalValue = "0.75";
                          else if (selectedSize === "1 1/4") decimalValue = "1.25";
                          else if (selectedSize === "1 1/2") decimalValue = "1.5";
                          else if (selectedSize === "2 1/2") decimalValue = "2.5";

                          else if (selectedSize === "4/5") decimalValue = "0.8";
                          else if (selectedSize === "1 4/7") decimalValue = "1.571";
                          else if (selectedSize === "3 1/2") decimalValue = "3.5";
                          else if (selectedSize === "4 1/3") decimalValue = "4.333";
                          else if (selectedSize === "5 1/2") decimalValue = "5.5";
                          else if (selectedSize === "6 2/7") decimalValue = "6.285714286";
                          else {

                            try {
                              const parts = selectedSize.split(' ');
                              if (parts.length === 2) {

                                const wholePart = parseInt(parts[0]);
                                const fractionParts = parts[1].split('/');
                                if (fractionParts.length === 2) {
                                  const numerator = parseInt(fractionParts[0]);
                                  const denominator = parseInt(fractionParts[1]);
                                  decimalValue = (wholePart + numerator / denominator).toString();
                                }
                              } else if (parts.length === 1 && selectedSize.includes('/')) {

                                const fractionParts = selectedSize.split('/');
                                if (fractionParts.length === 2) {
                                  const numerator = parseInt(fractionParts[0]);
                                  const denominator = parseInt(fractionParts[1]);
                                  decimalValue = (numerator / denominator).toString();
                                }
                              }
                            } catch (e) {
                              decimalValue = parseFloat(selectedSize) || selectedSize;
                            }
                          }
                        }
                        handleInputChange(`subMain${subMainNumber}OuterDia`, decimalValue);

                        setSubMainClassErrors((prev) => ({
                          ...prev,
                          [subMainNumber]: false,
                        }));
                        setSubMainErrorMessages((prev) => ({
                          ...prev,
                          [subMainNumber]: "",
                        }));
                      }}
                      disabled={!formData[`subMain${subMainNumber}Class`]}
                    >
                      <option value="">
                        {formData[`subMain${subMainNumber}Class`]
                          ? "Select pipe size"
                          : "Select pipe class first"}
                      </option>
                      {formData[`subMain${subMainNumber}Class`] &&
                      (!subMainFilteredSizes[subMainNumber] ||
                        subMainFilteredSizes[subMainNumber].length === 0) ? (
                        <option value="" disabled>
                          No sizes available for{" "}
                          {formData[`subMain${subMainNumber}Class`]}
                        </option>
                      ) : (
                        (subMainFilteredSizes[subMainNumber] || []).map(
                          (size) => {
                            const material = getMaterialFromClass(
                              formData[`subMain${subMainNumber}Class`]
                            );
                            const displaySize = getDisplaySizeForHDPE(
                              size,
                              material
                            );

                            const sizeValue = typeof size === 'object' ? size.inch.replace(/ /g, '_') : size.replace(/ /g, '_');
                            const sizeKey = typeof size === 'object' ? size.inch.replace(/ /g, '_') : size.replace(/ /g, '_');

                            return (
                              <option key={sizeKey} value={sizeValue}>
                                {displaySize}
                              </option>
                            );
                          }
                        )
                      )}
                    </select>
                    {!formData[`subMain${subMainNumber}Class`] && (
                      <div
                        style={{
                          marginTop: "6px",
                          fontSize: "11px",
                          color: "#6c757d",
                          fontStyle: "italic",
                        }}
                      >
                        💡 Select a pipe class above to see available sizes
                      </div>
                    )}
                    {formData[`subMain${subMainNumber}Class`] &&
                      (!subMainFilteredSizes[subMainNumber] ||
                        subMainFilteredSizes[subMainNumber].length === 0) && (
                        <div
                          style={{
                            marginTop: "6px",
                            padding: "8px 12px",
                            backgroundColor: "#fff3cd",
                            border: "1px solid #ffeaa7",
                            borderRadius: "4px",
                            fontSize: "12px",
                            color: "#856404",
                          }}
                        >
                          ⚠️ No pipe sizes available for class{" "}
                          {formData[`subMain${subMainNumber}Class`]}. Try
                          selecting a different class.
                        </div>
                      )}
                  </div>
                  <div>
                    <label
                      style={{
                        fontWeight: "500",
                        display: "block",
                        marginBottom: "5px",
                      }}
                    >
                      Sub Main Inner Dia (mm):
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData[`subMain${subMainNumber}InnerDia`] || ""}
                      readOnly
                      style={{
                        padding: "8px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        width: "100%",
                        backgroundColor: "#f5f5f5",
                        color: "#666",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        fontWeight: "500",
                        display: "block",
                        marginBottom: "5px",
                      }}
                    >
                      No. of Lateral/ Only Sub Main{subMainNumber}:
                    </label>
                    <input
                      type="number"
                      value={
                        formData[`subMain${subMainNumber}NoOfLateralOnly`] || ""
                      }
                      onChange={(e) =>
                        handleInputChange(
                          `subMain${subMainNumber}NoOfLateralOnly`,
                          e.target.value
                        )
                      }
                      style={{
                        padding: "8px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        width: "100%",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        fontWeight: "500",
                        display: "block",
                        marginBottom: "5px",
                      }}
                    >
                      Flow per Lateral (LPH):
                      <span
                        style={{
                          fontSize: "12px",
                          color: "#666",
                          fontWeight: "normal",
                        }}
                      >
                        {" "}
                        (Auto-calculated from lateral)
                      </span>
                    </label>
                    <input
                      type="number"
                      value={
                        formData[`subMain${subMainNumber}FlowPerLateral`] ||
                        (() => {
                          if (selectedOption === "drip-lateral") {
                            return formData.totalFlowPerLateral || "";
                          } else if (selectedOption === "drip-line") {
                            return formData.dripLineTotalFlow || "";
                          }
                          return "";
                        })()
                      }
                      onChange={(e) =>
                        handleInputChange(
                          `subMain${subMainNumber}FlowPerLateral`,
                          e.target.value
                        )
                      }
                      placeholder={(() => {
                        if (selectedOption === "drip-lateral") {
                          return formData.totalFlowPerLateral
                            ? `Auto: ${parseFloat(
                                formData.totalFlowPerLateral
                              ).toFixed(2)}`
                            : "Auto-calculated";
                        } else if (selectedOption === "drip-line") {
                          return formData.dripLineTotalFlow
                            ? `Auto: ${parseFloat(
                                formData.dripLineTotalFlow
                              ).toFixed(2)}`
                            : "Auto-calculated";
                        }
                        return "Auto-calculated";
                      })()}
                      style={{
                        padding: "8px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        width: "100%",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        fontWeight: "500",
                        display: "block",
                        marginBottom: "5px",
                      }}
                    >
                      Total Flow (LPH) per SM{subMainNumber}:
                    </label>
                    <input
                      type="number"
                      value={formData[`subMain${subMainNumber}TotalFlow`] || ""}
                      onChange={(e) =>
                        handleInputChange(
                          `subMain${subMainNumber}TotalFlow`,
                          e.target.value
                        )
                      }
                      style={{
                        padding: "8px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        width: "100%",
                      }}
                    />
                  </div>
                </div>

                {}
                <div
                  style={{
                    marginTop: "15px",
                    padding: "10px",
                    backgroundColor: "#f0f8ff",
                    borderRadius: "5px",
                    border: "1px solid #2196F3",
                  }}
                >
                  <h6 style={{ margin: "0 0 10px 0", color: "#2196F3" }}>
                    Sub Main {subMainNumber} Calculated Values:
                  </h6>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gap: "10px",
                      fontSize: "14px",
                    }}
                  >
                    <div>
                      <strong>Total Flow (LPH):</strong>
                      <br />
                      {calculatedValues.subMainTotalFlow[
                        subMainNumber
                      ]?.toFixed(2) || "0.00"}
                    </div>
                    <div>
                      <strong>Head Loss (m):</strong>
                      <br />
                      {calculatedValues.subMainHeadLoss[subMainNumber]?.toFixed(
                        2
                      ) || "0.00"}
                    </div>
                    <div>
                      <strong>Outlet Factor:</strong>
                      <br />
                      {calculatedValues.subMainOutletFactor[
                        subMainNumber
                      ]?.toFixed(3) || "0.000"}
                    </div>
                    <div>
                      <strong>Net Head Loss (m):</strong>
                      <br />
                      {calculatedValues.subMainNetHeadLoss[
                        subMainNumber
                      ]?.toFixed(2) || "0.00"}
                    </div>
                    <div>
                      <strong>Velocity (m/s):</strong>
                      <br />
                      {calculatedValues.subMainVelocity[
                        subMainNumber
                      ]?.toFixed(2) || "0.00"}
                      {calculatedValues.subMainVelocity[subMainNumber] > 1.5 && (
                        <div style={{ color: "red", fontSize: "12px", marginTop: "5px" }}>
                          Caution: Velocity is greater than 1.5m/s, consider readjustment
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {}
            <div style={sectionStyle}>
              <h4 style={{ marginBottom: "15px", color: "#555" }}>Main</h4>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "15px",
                }}
              >
                <div>
                  <label
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Main Length (m):
                  </label>
                  <input
                    type="number"
                    value={formData.mainLength}
                    onChange={(e) =>
                      handleInputChange("mainLength", e.target.value)
                    }
                    style={{
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "100%",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Pressure Class:
                  </label>
                  <select
                    style={{
                      padding: "8px",
                      border: `1px solid ${
                        mainClassError ? "#ff4d4f" : "#ddd"
                      }`,
                      borderRadius: "4px",
                      width: "100%",
                    }}
                    value={formData.mainClass}
                    onChange={(e) => {
                      const newClass = e.target.value;
                      handleInputChange("mainClass", newClass);
                      setMainClassError(false);
                      setMainErrorMessage("");
                    }}
                  >
                    <option value="">Select pipe class</option>

                    <optgroup label="uPVC Pipe">
                      {pipeClasses
                        .filter((cls) =>
                          ["CL6", "CL9", "CL12", "CL15"].includes(cls)
                        )
                        .map((cls) => (
                          <option key={cls} value={cls}>
                            {getClassDisplayName(cls)}
                          </option>
                        ))}
                    </optgroup>

                    <optgroup label="GI Pipe">
                      {pipeClasses
                        .filter((cls) => ["EL", "L2", "M", "H"].includes(cls))
                        .map((cls) => (
                          <option key={cls} value={cls}>
                            {getClassDisplayName(cls)}
                          </option>
                        ))}
                    </optgroup>

                    <optgroup label="HDPE Pipe">
                      {pipeClasses
                        .filter((cls) =>
                          [
                            "PN-06",
                            "PN-08",
                            "PN-10",
                            "PN-12.5",
                            "PN-16",
                          ].includes(cls)
                        )
                        .map((cls) => (
                          <option key={cls} value={cls}>
                            {getClassDisplayName(cls)}
                          </option>
                        ))}
                    </optgroup>
                  </select>
                  {mainClassError && (
                    <div
                      style={{
                        color: "#ff4d4f",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {mainErrorMessage}
                    </div>
                  )}
                </div>
                <div>
                  <label
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Diameter:
                    {formData.mainClass && (
                      <span
                        style={{
                          fontSize: "12px",
                          color: "#6c757d",
                          fontWeight: "400",
                          marginLeft: "8px",
                        }}
                      >
                        {getMaterialIcon(formData.mainClass)} filtered for{" "}
                        {formData.mainClass} (
                        {getMaterialDisplayName(formData.mainClass)})
                      </span>
                    )}
                  </label>
                  <select
                    style={{
                      padding: "8px",
                      border: `1px solid ${
                        mainClassError ? "#ff4d4f" : "#ddd"
                      }`,
                      borderRadius: "4px",
                      width: "100%",
                    }}
                    value={(formData.mainSize || "").replace(/ /g, '_')}
                    onChange={(e) => {

                      const selectedSize = e.target.value.replace(/_/g, ' ');

                      let decimalValue = selectedSize;
                      if (selectedSize.includes("/")) {

                        if (selectedSize === "1/2") decimalValue = "0.5";
                        else if (selectedSize === "3/4") decimalValue = "0.75";
                        else if (selectedSize === "1 1/4") decimalValue = "1.25";
                        else if (selectedSize === "1 1/2") decimalValue = "1.5";
                        else if (selectedSize === "2 1/2") decimalValue = "2.5";

                        else if (selectedSize === "4/5") decimalValue = "0.8";
                        else if (selectedSize === "1 4/7") decimalValue = "1.571";
                        else if (selectedSize === "3 1/2") decimalValue = "3.5";
                        else if (selectedSize === "4 1/3") decimalValue = "4.333";
                        else if (selectedSize === "5 1/2") decimalValue = "5.5";
                        else if (selectedSize === "6 2/7") decimalValue = "6.285714286";
                        else {

                          try {
                            const parts = selectedSize.split(' ');
                            if (parts.length === 2) {

                              const wholePart = parseInt(parts[0]);
                              const fractionParts = parts[1].split('/');
                              if (fractionParts.length === 2) {
                                const numerator = parseInt(fractionParts[0]);
                                const denominator = parseInt(fractionParts[1]);
                                decimalValue = (wholePart + numerator / denominator).toString();
                              }
                            } else if (parts.length === 1 && selectedSize.includes('/')) {

                              const fractionParts = selectedSize.split('/');
                              if (fractionParts.length === 2) {
                                const numerator = parseInt(fractionParts[0]);
                                const denominator = parseInt(fractionParts[1]);
                                decimalValue = (numerator / denominator).toString();
                              }
                            }
                          } catch (e) {
                            decimalValue = parseFloat(selectedSize) || selectedSize;
                          }
                        }
                      }

                      handleInputChange("mainSize", selectedSize);
                      handleInputChange("mainOuterDia", decimalValue);
                      setMainClassError(false);
                      setMainErrorMessage("");
                    }}
                    disabled={!formData.mainClass}
                  >
                    <option value="">
                      {formData.mainClass
                        ? "Select pipe size"
                        : "Select pipe class first"}
                    </option>
                    {formData.mainClass && mainFilteredSizes.length === 0 ? (
                      <option value="" disabled>
                        No sizes available for {formData.mainClass}
                      </option>
                    ) : (
                      mainFilteredSizes.map((size) => {
                        const material = getMaterialFromClass(
                          formData.mainClass
                        );
                        const displaySize = getDisplaySizeForHDPE(
                          size,
                          material
                        );

                        const sizeValue = typeof size === 'object' ? size.inch.replace(/ /g, '_') : size.replace(/ /g, '_');
                        const sizeKey = typeof size === 'object' ? size.inch.replace(/ /g, '_') : size.replace(/ /g, '_');

                        return (
                          <option key={sizeKey} value={sizeValue}>
                            {displaySize}
                          </option>
                        );
                      })
                    )}
                  </select>
                  {!formData.mainClass && (
                    <div
                      style={{
                        marginTop: "6px",
                        fontSize: "11px",
                        color: "#6c757d",
                        fontStyle: "italic",
                      }}
                    >
                      💡 Select a pipe class above to see available sizes
                    </div>
                  )}
                  {formData.mainClass && mainFilteredSizes.length === 0 && (
                    <div
                      style={{
                        marginTop: "6px",
                        padding: "8px 12px",
                        backgroundColor: "#fff3cd",
                        border: "1px solid #ffeaa7",
                        borderRadius: "4px",
                        fontSize: "12px",
                        color: "#856404",
                      }}
                    >
                      ⚠️ No pipe sizes available for class {formData.mainClass}.
                      Try selecting a different class.
                    </div>
                  )}
                </div>
                <div>
                  <label
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Main Inner Dia (mm):
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.mainInnerDia}
                    readOnly
                    style={{
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "100%",
                      backgroundColor: "#f5f5f5",
                      color: "#666",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Total Flow (LPH) on Main:
                  </label>
                  <input
                    type="number"
                    value={formData.totalFlowMain}
                    onChange={(e) =>
                      handleInputChange("totalFlowMain", e.target.value)
                    }
                    style={{
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "100%",
                    }}
                  />
                </div>
              </div>

              {}
              <div
                style={{
                  marginTop: "15px",
                  padding: "10px",
                  backgroundColor: "#f0f8ff",
                  borderRadius: "5px",
                  border: "1px solid #2196F3",
                }}
              >
                <h5 style={{ margin: "0 0 10px 0", color: "#2196F3" }}>
                  Main Pipe Calculated Values:
                </h5>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: "10px",
                    fontSize: "14px",
                  }}
                >
                  <div>
                    <strong>Total Flow (LPH):</strong>
                    <br />
                    {formData.totalFlowMain || "0"}
                  </div>
                  <div>
                    <strong>Head Loss (m):</strong>
                    <br />
                    {calculatedValues.mainHeadLoss?.toFixed(2) || "0.00"}
                  </div>
                  <div>
                    <strong>Net Head Loss (m):</strong>
                    <br />
                    {calculatedValues.mainNetHeadLoss?.toFixed(2) || "0.00"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </>

      {}

      {}
      <div style={sectionStyle}>
        <h4 style={{ marginBottom: "15px", color: "#555" }}>LPH Comparison</h4>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "15px",
            marginBottom: "15px",
          }}
        >
          <div
            style={{
              padding: "10px",
              backgroundColor: "#e8f5e8",
              borderRadius: "5px",
              border: "1px solid #4CAF50",
            }}
          >
            <div
              style={{
                fontWeight: "500",
                marginBottom: "5px",
                color: "#2e7d32",
              }}
            >
              Crop LPH Requirement:
            </div>
            <div
              style={{ fontSize: "18px", fontWeight: "bold", color: "#2e7d32" }}
            >
              {(lph || 0).toFixed(2)} LPH
            </div>
          </div>
          <div
            style={{
              padding: "10px",
              backgroundColor: "#f0f8ff",
              borderRadius: "5px",
              border: "1px solid #2196F3",
            }}
          >
            <div
              style={{
                fontWeight: "500",
                marginBottom: "5px",
                color: "#1976d2",
              }}
            >
              System Design LPH:
            </div>
            <div
              style={{ fontSize: "18px", fontWeight: "bold", color: "#1976d2" }}
            >
              {(() => {

                let systemLph = 0;

                subMains.forEach((subMainNumber) => {
                  const subMainTotalFlow =
                    calculatedValues.subMainTotalFlow?.[subMainNumber] || 0;
                  systemLph = Math.max(systemLph, subMainTotalFlow);
                });

                return systemLph.toFixed(2);
              })()}{" "}
              LPH
            </div>
          </div>
        </div>

        {}
        {(() => {

          let systemLph = 0;
          subMains.forEach((subMainNumber) => {
            const subMainTotalFlow =
              calculatedValues.subMainTotalFlow?.[subMainNumber] || 0;
            systemLph = Math.max(systemLph, subMainTotalFlow);
          });

          const cropLph = lph || 0;

          if (systemLph > cropLph && systemLph > 0 && cropLph > 0) {
            return (
              <div
                style={{
                  padding: "10px",
                  backgroundColor: "#fff3cd",
                  border: "1px solid #ffc107",
                  borderRadius: "5px",
                  color: "#856404",
                  fontWeight: "500",
                }}
              >
                ⚠️ Designed system LPH is more than selected crop LPH per day
              </div>
            );
          }
          return null;
        })()}
      </div>

      {}
      <div style={sectionStyle}>
        <h4 style={{ marginBottom: "15px", color: "#555" }}>
          System Parameters
        </h4>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "15px",
          }}
        >
          <div>
            <label
              style={{
                fontWeight: "500",
                display: "block",
                marginBottom: "5px",
              }}
            >
              Nozzle Pressure (m):
            </label>
            <input
              type="number"
              step="0.1"
              value={formData.nozzlePressure || ""}
              onChange={(e) =>
                handleInputChange("nozzlePressure", e.target.value)
              }
              style={{
                padding: "8px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                width: "100%",
              }}
            />
          </div>
          <div>
            <label
              style={{
                fontWeight: "500",
                display: "block",
                marginBottom: "5px",
              }}
            >
              Elevation Difference (if any within the field):
            </label>
            <input
              type="number"
              step="0.1"
              value={formData.elevationDifference || ""}
              onChange={(e) =>
                handleInputChange("elevationDifference", e.target.value)
              }
              style={{
                padding: "8px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                width: "100%",
              }}
            />
          </div>
          <div>
            <label
              style={{
                fontWeight: "500",
                display: "block",
                marginBottom: "5px",
              }}
            >
              Friction Losses in Filteration System:
            </label>
            <input
              type="number"
              step="0.1"
              value={formData.filtrationLosses || ""}
              onChange={(e) =>
                handleInputChange("filtrationLosses", e.target.value)
              }
              style={{
                padding: "8px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                width: "100%",
              }}
            />
          </div>
          <div>
            <label
              style={{
                fontWeight: "500",
                display: "block",
                marginBottom: "5px",
              }}
            >
              Friction Losses in Fertigation System:
            </label>
            <input
              type="number"
              step="0.1"
              value={formData.fertigationLosses || ""}
              onChange={(e) =>
                handleInputChange("fertigationLosses", e.target.value)
              }
              style={{
                padding: "8px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                width: "100%",
              }}
            />
          </div>
        </div>

        {}
        <h5 style={{ marginTop: "20px", marginBottom: "15px", color: "#555" }}>
          Efficiency Parameters
        </h5>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: "15px",
          }}
        >
          <div>
            <label
              style={{
                fontWeight: "500",
                display: "block",
                marginBottom: "5px",
              }}
            >
              Pump Efficiency (%):
            </label>
            <input
              type="number"
              min="1"
              max="100"
              value={pumpEfficiency}
              onChange={(e) =>
                setPumpEfficiency(parseFloat(e.target.value) || 70)
              }
              style={{
                padding: "8px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                width: "100%",
              }}
            />
          </div>
          <div>
            <label
              style={{
                fontWeight: "500",
                display: "block",
                marginBottom: "5px",
              }}
            >
              Motor Efficiency (%):
            </label>
            <input
              type="number"
              min="1"
              max="100"
              value={motorEfficiency}
              onChange={(e) =>
                setMotorEfficiency(parseFloat(e.target.value) || 85)
              }
              style={{
                padding: "8px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                width: "100%",
              }}
            />
          </div>
          <div>
            <label
              style={{
                fontWeight: "500",
                display: "block",
                marginBottom: "5px",
              }}
            >
              Solar Efficiency (%):
            </label>
            <input
              type="number"
              min="1"
              max="100"
              value={solarEfficiency}
              onChange={(e) =>
                setSolarEfficiency(parseFloat(e.target.value) || 70)
              }
              style={{
                padding: "8px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                width: "100%",
              }}
            />
          </div>
        </div>

        {}
        <div style={{ marginTop: "20px", textAlign: "center" }}>
          <button
            onClick={() => {
              const powerCalcs = calculatePowerRequirements();
              if (powerCalcs && onCalculationResults) {

                const cropLph = lph || 0;
                onCalculationResults(cropLph, powerCalcs);
              }
            }}
            disabled={!calculatedValues.tdh || !calculatedValues.systemLph}
            style={{
              padding: "12px 24px",
              backgroundColor:
                !calculatedValues.tdh || !calculatedValues.systemLph
                  ? "#ccc"
                  : "#4CAF50",
              color: "white",
              border: "none",
              borderRadius: "6px",
              fontSize: "16px",
              fontWeight: "500",
              cursor:
                !calculatedValues.tdh || !calculatedValues.systemLph
                  ? "not-allowed"
                  : "pointer",
              transition: "background-color 0.3s",
            }}
          >
            Calculate Power Requirements
          </button>
        </div>
      </div>

      {}
      <div style={sectionStyle}>
        <h4 style={{ marginBottom: "15px", color: "#555" }}>
          System Calculations & TDH Summary
        </h4>

        {}
        <div
          style={{
            padding: "15px",
            backgroundColor: "#f8f9fa",
            borderRadius: "5px",
            border: "1px solid #dee2e6",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              fontSize: "14px",
            }}
          >
            <div>
              <strong>Major Friction Losses (m):</strong>
              <br />
              {calculatedValues.majorFrictionLosses?.toFixed(2) || "0.00"}
            </div>
            <div>
              <strong>Minor Friction Losses (m):</strong>
              <br />
              {calculatedValues.minorFrictionLosses?.toFixed(2) || "0.00"}
            </div>
            <div>
              <strong>Total Friction Losses (m):</strong>
              <br />
              {calculatedValues.totalFrictionLosses?.toFixed(2) || "0.00"}
            </div>
          </div>
        </div>

        {}
        <div
          style={{
            padding: "5px",
            backgroundColor: "#e8f5e8",
            borderRadius: "5px",
            border: "2px solid #4CAF50",
          }}
        >
          <h5 style={{ margin: "0 0 15px 0", color: "#2e7d32" }}>
            Total Dynamic Head (TDH) Calculation:
          </h5>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "10px",
              fontSize: "14px",
              marginBottom: "15px",
            }}
          >
            <div>
              Major Friction losses in piping network:{" "}
              <strong>
                {calculatedValues.majorFrictionLosses?.toFixed(2) || "0.00"} m
              </strong>
            </div>
            <div>
              Minor Fricton losses in piping network:{" "}
              <strong>
                {calculatedValues.minorFrictionLosses?.toFixed(2) || "0.00"} m
              </strong>
            </div>
            <div>
              Total Friction Losses:{" "}
              <strong>
                {calculatedValues.totalFrictionLosses?.toFixed(2) || "0.00"} m
              </strong>
            </div>
          </div>

          {}
          <div style={{
            borderTop: "2px solid #4CAF50",
            paddingTop: "15px",
            marginTop: "15px",
          }}>
            {}
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "8px",
              backgroundColor: "#e3f2fd",
              borderRadius: "4px",
              marginBottom: "8px",
              border: "1px solid #2196F3",
            }}>
              <span style={{ color: "#1976d2", fontWeight: "500" }}>
                Pump TDH ({selectedPump === "surface" ? "Surface" : "Submersible"}):
              </span>
              <span style={{ color: "#1976d2", fontWeight: "bold" }}>
                {calculatePumpTDH().toFixed(2)} m
              </span>
            </div>

            {}
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "8px",
              backgroundColor: "#f3e5f5",
              borderRadius: "4px",
              marginBottom: "8px",
              border: "1px solid #9c27b0",
            }}>
              <span style={{ color: "#7b1fa2", fontWeight: "500" }}>
                Irrigation System TDH:
              </span>
              <span style={{ color: "#7b1fa2", fontWeight: "bold" }}>
                {(() => {
                  const irrigationTdh = (calculatedValues.tdh || 0) - calculatePumpTDH();
                  return irrigationTdh.toFixed(2);
                })()} m
              </span>
            </div>

            {}
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "12px",
              backgroundColor: "#e8f5e8",
              borderRadius: "4px",
              border: "2px solid #4CAF50",
              fontSize: "18px",
              fontWeight: "bold",
              color: "#2e7d32",
            }}>
              <span>Total TDH:</span>
              <span>{calculatedValues.tdh?.toFixed(2) || "0.00"} m</span>
            </div>
          </div>

          {}
          {calculatedValues.tdh > 50 && (
            <div
              style={{
                marginTop: "10px",
                padding: "8px",
                backgroundColor: "#ffebee",
                border: "1px solid #f44336",
                borderRadius: "4px",
                color: "#c62828",
                fontSize: "12px",
              }}
            >
              ⚠️ High TDH detected. Consider system optimization.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Scenario3;
